<template>
  <header-block :page="activePage.name">
    <template #center>
      <nav class="pill-nav pill-nav-icons device-switcher">
        <button
          :class="{ active: size === 'wide' }"
          title="Desktop"
          v-on:click="size = 'wide'"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path
              d="M4 5v11h16V5H4zm-2-.993C2 3.451 2.455 3 2.992 3h18.016c.548 0 .992.449.992 1.007V18H2V4.007zM1 19h22v2H1v-2z"
            />
          </svg>
        </button>
        <button
          :class="{ active: size === 'tablet' }"
          title="Tablet"
          v-on:click="size = 'tablet'"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path
              d="M3 6a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-12z"
            />
            <path d="M9 17h6" />
          </svg>
        </button>
        <button
          :class="{ active: size === 'phone' }"
          title="Phone"
          v-on:click="size = 'phone'"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path
              d="M7 4v16h10V4H7zM6 2h12a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm6 15a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
            />
          </svg>
        </button>
      </nav>
    </template>
    <nav class="pill-nav pill-nav-icons">
      <button
        :disabled="undoStack.length === 0"
        title="Undo (Ctrl+Z)"
        v-on:click="undo"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="17"
          height="17"
          fill="currentColor"
        >
          <path
            d="M8 7v4L2 6l6-5v4h5a8 8 0 1 1 0 16H4v-2h9a6 6 0 1 0 0-12H8z"
          />
        </svg>
      </button>
      <button
        :disabled="redoStack.length === 0"
        title="Redo (Ctrl+Y)"
        v-on:click="redo"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="17"
          height="17"
          fill="currentColor"
        >
          <path
            d="M16 7v4l6-5-6-5v4h-5a8 8 0 1 0 0 16h9v-2h-9a6 6 0 1 1 0-12h5z"
          />
        </svg>
      </button>
    </nav>
    <select class="form-control lang-selector" v-model="language">
      <option v-for="lang in languages" :key="lang.code" :value="lang.code">
        {{ lang.name }}
      </option>
    </select>
    <!-- A page starts as All webshops and stays one page. Only regions that
         were deliberately given content of their own appear here: there is
         nothing to "switch to" for the others, they are the All webshops
         page. New ones are carved out with the + button. -->
    <select
      class="form-control lang-selector"
      :value="regionChoice === null ? '' : String(regionChoice)"
      v-on:change="onRegionChange($event.target.value)"
      v-if="existingRegions.length > 1"
      title="Which version of this page you are editing"
    >
      <option
        v-for="region in existingRegions"
        :key="region.key"
        :value="region.key"
      >
        {{ region.label }}
      </option>
    </select>
    <div class="region-add" v-if="addableRegions.length > 0">
      <button
        class="icon-btn"
        :title="'Give one region its own version of this page'"
        v-on:click="regionMenuOpen = !regionMenuOpen"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path
            d="M11 11V7h2v4h4v2h-4v4h-2v-4H7v-2h4zm1 11C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"
          />
        </svg>
      </button>
      <div class="region-menu" v-if="regionMenuOpen">
        <div class="region-menu-head">Own version for…</div>
        <button
          v-for="shop in addableRegions"
          :key="shop.id"
          v-on:click="addRegion(shop)"
        >
          {{ shop.name }}
        </button>
      </div>
    </div>
    <span class="save-status">{{ saveStatus }}</span>
    <span class="badge-soft badge-yellow" v-if="dirty"
      >Unpublished changes</span
    >
    <button
      class="icon-btn"
      title="Version history"
      v-on:click="historyShow = true"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="19"
        height="19"
        fill="currentColor"
      >
        <path
          d="M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12h2a8 8 0 1 0 .5-2.794L6.707 11.4 5.293 12.813 1 8.52l4.293-4.293L6.707 5.64 4.61 7.737A9.978 9.978 0 0 1 12 2zm1 5v4.585l3.243 3.243-1.415 1.415L11 12.413V7h2z"
        />
      </svg>
    </button>
    <button class="btn btn-primary" :disabled="saving" v-on:click="save()">
      {{ saving ? $t("editor.saving") : $t("editor.save") }}
    </button>
    <button
      class="icon-btn"
      title="Global settings"
      v-on:click="changeGlobalSettingsModal"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="currentColor"
      >
        <path
          d="M2 12c0-.865.11-1.703.316-2.504A3 3 0 0 0 4.99 4.867a9.99 9.99 0 0 1 4.335-2.505 3 3 0 0 0 5.348 0 9.99 9.99 0 0 1 4.335 2.505 3 3 0 0 0 2.675 4.63c.206.8.316 1.638.316 2.503 0 .865-.11 1.703-.316 2.504a3 3 0 0 0-2.675 4.629 9.99 9.99 0 0 1-4.335 2.505 3 3 0 0 0-5.348 0 9.99 9.99 0 0 1-4.335-2.505 3 3 0 0 0-2.675-4.63C2.11 13.704 2 12.866 2 12zm4.804 3c.63 1.091.81 2.346.564 3.524.408.29.842.541 1.297.75A4.993 4.993 0 0 1 12 18c1.26 0 2.438.471 3.335 1.274.455-.209.889-.46 1.297-.75A4.993 4.993 0 0 1 17.196 15a4.993 4.993 0 0 1 2.77-2.25 8.126 8.126 0 0 0 0-1.5A4.993 4.993 0 0 1 17.195 9a4.993 4.993 0 0 1-.564-3.524 7.989 7.989 0 0 0-1.297-.75A4.993 4.993 0 0 1 12 6a4.993 4.993 0 0 1-3.335-1.274 7.99 7.99 0 0 0-1.297.75A4.993 4.993 0 0 1 6.804 9a4.993 4.993 0 0 1-2.77 2.25 8.126 8.126 0 0 0 0 1.5A4.993 4.993 0 0 1 6.805 15zM12 15a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm0-2a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
        />
      </svg>
    </button>
  </header-block>
  <div class="editor-layout">
    <aside class="editor-left">
      <div class="white-box">
        <div class="row align-items-center mb-2 gx-0">
          <div class="col">
            <h1>{{ $t("editor.blocks") }}</h1>
          </div>
          <div class="col-auto" v-if="blocksWithGaps > 0">
            <span
              class="badge-soft badge-yellow"
              :title="
                'These blocks have a picture with no alt text, or an image ' +
                'field with no picture, in ' +
                (languageName ?? 'this language')
              "
            >
              {{ blocksWithGaps }} SEO
              {{ blocksWithGaps === 1 ? "gap" : "gaps" }}
            </span>
          </div>
          <div class="col-auto">
            <button
              class="icon-btn icon-btn-accent"
              title="Add block"
              v-on:click="openAddBlock()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="currentColor"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path
                  d="M11 11V7h2v4h4v2h-4v4h-2v-4H7v-2h4zm1 11C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"
                />
              </svg>
            </button>
          </div>
        </div>
        <draggable
          v-if="blocks.length > 0"
          class="drag-area blocks"
          tag="ul"
          :list="blocks"
          :group="{ name: 'g1' }"
          item-key="key"
          v-on:end="moveEvent"
          v-bind="dragOptions"
        >
          <template #item="{ element }">
            <li
              :class="{
                selected: selectedBlock?.key === element.key,
                'hidden-block': element?.value?.hidden,
                'off-device-row': isHiddenOnCurrentDevice(element),
              }"
            >
              <div class="block-row" v-on:click="showEditBlock(element, true)">
                <svg
                  class="drag-handle"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="currentColor"
                >
                  <circle cx="9" cy="5" r="1.6" />
                  <circle cx="15" cy="5" r="1.6" />
                  <circle cx="9" cy="12" r="1.6" />
                  <circle cx="15" cy="12" r="1.6" />
                  <circle cx="9" cy="19" r="1.6" />
                  <circle cx="15" cy="19" r="1.6" />
                </svg>
                <span
                  v-html="blockIcon(element.typeName, element?.type?.icon)"
                  class="block-icon"
                ></span>
                <span class="block-name">{{
                  element?.value?.block_name ??
                  element?.type?.name ??
                  element.key
                }}</span>
                <!-- An empty heading or image is invisible in this list but
                     very visible on the page, so the row says so. -->
                <span
                  v-if="blockGaps(element).length > 0"
                  class="gap-badge"
                  :title="blockGapTitle(element)"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="13"
                    height="13"
                    fill="currentColor"
                  >
                    <path
                      d="M12.866 3l9.526 16.5a1 1 0 0 1-.866 1.5H2.474a1 1 0 0 1-.866-1.5L11.134 3a1 1 0 0 1 1.732 0zM11 16v2h2v-2h-2zm0-7v5h2V9h-2z"
                    />
                  </svg>
                  <!-- Almost every block has exactly one gap, and the "1" cost
                       the block's own name the room it needed to stay
                       readable. The number earns its place only above one. -->
                  <template v-if="blockGaps(element).length > 1">{{
                    blockGaps(element).length
                  }}</template>
                </span>
                <span
                  v-if="blockVisibility(element)"
                  class="device-badge"
                  :class="{ 'off-device': isHiddenOnCurrentDevice(element) }"
                  :title="
                    (blockVisibility(element) === 'phone'
                      ? 'Only visible on phone'
                      : 'Only visible on desktop') +
                    (isHiddenOnCurrentDevice(element)
                      ? ' — hidden in the current preview'
                      : '')
                  "
                >
                  <svg
                    v-if="blockVisibility(element) === 'phone'"
                    viewBox="0 0 24 24"
                    width="13"
                    height="13"
                    fill="currentColor"
                  >
                    <path
                      d="M7 4v16h10V4H7zM6 2h12a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zm6 15a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
                    />
                  </svg>
                  <svg
                    v-else
                    viewBox="0 0 24 24"
                    width="13"
                    height="13"
                    fill="currentColor"
                  >
                    <path
                      d="M4 5v11h16V5H4zm-2-.993C2 3.451 2.455 3 2.992 3h18.016c.548 0 .992.449.992 1.007V18H2V4.007zM1 19h22v2H1v-2z"
                    />
                  </svg>
                </span>
                <button
                  v-if="blockProducts(element).length > 0"
                  class="icon-btn chevron"
                  :class="{ open: expandedBlocks[element.key] }"
                  :title="
                    expandedBlocks[element.key]
                      ? 'Hide products'
                      : 'Show products'
                  "
                  v-on:click.stop="toggleExpand(element.key)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    fill="currentColor"
                  >
                    <path
                      d="M12 13.172l4.95-4.95 1.414 1.414L12 16 5.636 9.636 7.05 8.222z"
                    />
                  </svg>
                </button>
                <button
                  class="icon-btn"
                  :title="element?.value?.hidden ? 'Show block' : 'Hide block'"
                  v-on:click.stop="toggleHidden(element)"
                >
                  <svg
                    v-if="!element?.value?.hidden"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    fill="currentColor"
                  >
                    <path
                      d="M12 3c5.392 0 9.878 3.88 10.819 9-.94 5.12-5.427 9-10.819 9-5.392 0-9.878-3.88-10.818-9C2.122 6.88 6.608 3 12 3zm0 16c4.235 0 7.86-2.948 8.777-7C19.86 7.948 16.235 5 12 5c-4.236 0-7.86 2.948-8.777 7C4.14 16.052 7.764 19 12 19zm0-2.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-2a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z"
                    />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    fill="currentColor"
                  >
                    <path
                      d="M17.882 19.297A10.949 10.949 0 0 1 12 21c-5.392 0-9.878-3.88-10.819-9a10.982 10.982 0 0 1 3.34-6.066L1.392 2.808l1.415-1.415 19.799 19.8-1.415 1.414-3.31-3.31zM5.935 7.35A8.965 8.965 0 0 0 3.223 12a9.005 9.005 0 0 0 13.201 5.838l-2.028-2.028A4.5 4.5 0 0 1 8.19 9.604L5.935 7.35zm6.979 6.978l-3.242-3.242a2.5 2.5 0 0 0 3.241 3.241zm7.893 2.264l-1.431-1.43A8.935 8.935 0 0 0 20.777 12 9.005 9.005 0 0 0 9.552 5.338L7.974 3.76C9.221 3.27 10.58 3 12 3c5.392 0 9.878 3.88 10.819 9a10.947 10.947 0 0 1-2.012 4.592zm-9.084-9.084a4.5 4.5 0 0 1 4.769 4.769l-4.77-4.769z"
                    />
                  </svg>
                </button>
              </div>
              <ul class="block-children" v-if="expandedBlocks[element.key]">
                <li
                  v-for="product in blockProducts(element)"
                  :key="product.variant_id ?? product.id"
                >
                  <img
                    :src="productImage(product)"
                    :alt="product.name"
                    loading="lazy"
                  />
                  <span class="child-name">{{ product.name }}</span>
                  <span class="child-price">{{
                    formatPrice(product.price)
                  }}</span>
                </li>
              </ul>
            </li>
          </template>
        </draggable>
        <div class="empty-state" v-else-if="loaded.content">
          <div class="empty-icon">
            <svg viewBox="0 0 24 24">
              <path
                fill-rule="evenodd"
                d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm8-2h8v8h-8v-8zm2 2v4h4v-4h-4z"
              />
            </svg>
          </div>
          <div class="empty-title">No blocks yet</div>
          <div class="empty-text">
            This page is empty. Add your first block to start building.
          </div>
          <button class="btn btn-primary" v-on:click="openAddBlock()">
            Add block
          </button>
        </div>
      </div>
    </aside>
    <main class="editor-main preview-container" ref="outsideContainer">
      <div
        ref="container"
        class="iframe-container"
        :class="[size]"
        :style="`transform: scale(${rate});${
          height ? ' height:' + height + 'px;' : ''
        }`"
      >
        <div class="browser-chrome" v-if="size === 'wide'">
          <div class="chrome-tab" v-on:click="showEditPage">
            <div class="chrome-tab-icon"></div>
            <span class="chrome-tab-title">{{
              resolveText(activePage.title)
            }}</span>
            <span class="chrome-tab-close">×</span>
          </div>
          <div class="chrome-domain">
            <span class="chrome-domain-main">{{ liveDomain }}</span>
            <span class="chrome-domain-sub">{{ previewDomain }}</span>
          </div>
          <div class="chrome-dots">
            <span class="chrome-dot d3"></span>
            <span class="chrome-dot d2"></span>
            <span class="chrome-dot d1"></span>
          </div>
        </div>
        <div class="phone-statusbar" v-if="size === 'phone'">
          <span class="ps-time">{{ phoneTime }}</span>
          <span class="ps-island"></span>
          <span class="ps-icons">
            <svg viewBox="0 0 18 12" width="17" height="11" fill="currentColor">
              <rect x="0" y="8" width="3" height="4" rx="1" />
              <rect x="5" y="5.5" width="3" height="6.5" rx="1" />
              <rect x="10" y="3" width="3" height="9" rx="1" />
              <rect x="15" y="0" width="3" height="12" rx="1" />
            </svg>
            <svg viewBox="0 0 16 12" width="15" height="11" fill="currentColor">
              <path
                d="M8 9.5a1.7 1.7 0 1 1 0 3.4 1.7 1.7 0 0 1 0-3.4zM8 5.2c1.9 0 3.6.77 4.84 2.01l-1.42 1.42A4.83 4.83 0 0 0 8 7.2c-1.34 0-2.55.54-3.42 1.43L3.16 7.2A6.83 6.83 0 0 1 8 5.2zM8 .8c3.1 0 5.9 1.26 7.93 3.29l-1.42 1.42A9.17 9.17 0 0 0 8 2.8c-2.55 0-4.85 1.03-6.51 2.7L.07 4.1A11.16 11.16 0 0 1 8 .8z"
              />
            </svg>
            <svg viewBox="0 0 25 12" width="24" height="12">
              <rect
                x="0.5"
                y="0.5"
                width="21"
                height="11"
                rx="3"
                fill="none"
                stroke="currentColor"
                stroke-opacity="0.4"
              />
              <rect
                x="2"
                y="2"
                width="15"
                height="8"
                rx="1.6"
                fill="currentColor"
              />
              <path
                d="M23 4v4a2.2 2.2 0 0 0 0-4z"
                fill="currentColor"
                fill-opacity="0.4"
              />
            </svg>
          </span>
        </div>
        <div class="phone-home" v-if="size === 'phone'"></div>
        <iframe
          ref="preview"
          :style="[
            height ? 'height: ' + height + 'px' : '',
            zoom ? 'zoom: ' + zoom : '',
          ]"
        ></iframe>
      </div>
      <div
        class="preview-loading"
        :class="{ show: !loaded.style || !loaded.script || !loaded.page }"
      >
        <div class="preview-progress">
          <div
            class="preview-progress-bar"
            :style="`width: ${Math.min(loaded.var, 100)}%`"
          ></div>
        </div>
        <div class="preview-skeleton">
          <div class="skeleton sk-hero"></div>
          <div class="skeleton sk-line w-50"></div>
          <div class="skeleton sk-line w-70"></div>
          <div class="skeleton sk-box"></div>
        </div>
      </div>
    </main>
    <aside class="editor-right" v-if="selectedBlock">
      <section class="white-box">
        <div class="module-head">
          <span
            class="module-icon"
            v-html="
              blockIcon(selectedBlock?.typeName, selectedBlock?.type?.icon)
            "
          ></span>
          <div class="module-title">
            <div class="section-label">Module</div>
            <h2
              v-on:click="startEditBlockName"
              class="editable-text"
              title="Click to rename"
              v-if="!editBlockName"
            >
              {{
                selectedBlock?.value?.block_name ?? selectedBlock?.type?.name
              }}
            </h2>
            <input
              type="text"
              class="form-control"
              v-model="selectedBlock.value.block_name"
              v-else
              @blur="editBlockName = false"
              @keyup.enter="editBlockName = false"
            />
          </div>
          <div class="module-actions">
            <button
              class="icon-btn icon-btn-accent"
              title="Edit content"
              v-if="
                selectedBlock?.type?.blocks?.some(
                  (block) => block.type !== 'array'
                )
              "
              v-on:click="
                editContent(
                  selectedBlock.type.blocks,
                  selectedBlock.value.settings
                )
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path
                  d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4"
                />
                <path d="M13.5 6.5l4 4" />
              </svg>
            </button>
            <button
              class="icon-btn icon-btn-accent"
              title="Duplicate block"
              v-on:click="duplicateBlock"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path
                  d="M7 6V3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-3v3c0 .552-.45 1-1.007 1H4.007A1 1 0 0 1 3 21l.003-14c0-.552.45-1 1.007-1H7zM5.003 8L5 20h10V8H5.003zM9 6h8v10h2V4H9v2z"
                />
              </svg>
            </button>
            <button
              class="icon-btn icon-btn-danger"
              title="Delete block"
              v-on:click="deleteBlock"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="currentColor"
              >
                <path
                  d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"
                />
              </svg>
            </button>
          </div>
        </div>
      </section>
      <!-- Rules need page data to test against; static pages expose none, so
           the panel would just be an empty dropdown there. Still shown if the
           block already carries conditions, so nothing becomes unreachable. -->
      <div
        class="white-box"
        v-if="
          activePossibleVariables.length > 0 ||
          selectedBlock.value.conditions?.conditions?.length > 0
        "
      >
        <div class="row align-items-center mb-2 gx-0">
          <div class="col">
            <h1>{{ $t("editor.visibility") }}</h1>
          </div>
        </div>
        <div class="hint-text mb-2">
          Show this block only when the page data matches these rules.
        </div>
        <ConditionGroupEditor
          :variables="activePossibleVariables"
          :categories="conditionCategories"
          v-model="this.selectedBlock.value.conditions"
        />
      </div>
      <div class="white-box styles-block">
        <div class="row mb-2 gx-0">
          <div class="col-12">
            <h1>{{ $t("editor.style") }}</h1>
          </div>
        </div>
        <div
          class="row"
          v-for="block in selectedBlock?.type?.blocks.filter(
            (e) => e.styles?.length > 0
          )"
          :key="block.key"
        >
          <div class="col-12">
            <h3>{{ fieldLabel(block) }}</h3>
          </div>
          <template v-for="style in block.styles" :key="style">
            <div class="col-12 mb-2">
              <!-- A handful of choices reads better as one row of buttons
                   than as a dropdown you have to open to see the options. -->
              <div
                class="style-field"
                v-if="style.classes && styleOptions(style).length <= 3"
              >
                <span class="style-field-label">{{ style.name }}</span>
                <div class="seg" role="radiogroup" :aria-label="style.name">
                  <button
                    type="button"
                    v-for="opt in styleOptions(style)"
                    :key="opt.value"
                    class="seg-btn"
                    :class="{ active: styleValue(style) === opt.value }"
                    :aria-pressed="styleValue(style) === opt.value"
                    v-on:click="setStyleValue(style, opt.value)"
                  >
                    {{ opt.label }}
                  </button>
                </div>
              </div>
              <div class="input-group" v-else-if="style.classes">
                <label class="half">{{ style.name }}</label>
                <select
                  :value="styleValue(style)"
                  v-on:change="setStyleValue(style, $event.target.value)"
                >
                  <option
                    v-for="opt in styleOptions(style)"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div class="input-group" v-else>
                <input
                  type="checkbox"
                  :checked="
                    selectedBlock.value.settings[style.key] === style.class
                  "
                  :value="style.class"
                  v-on:change="toggleStyle(style)"
                />
                <label>{{ style.name }}</label>
              </div>
            </div>
          </template>
        </div>
        <!-- Closed by default: it applies to every block but is rarely
             touched, and open it pushed the block's own settings off screen. -->
        <div class="row">
          <div class="col-12">
            <button
              type="button"
              class="disclosure"
              v-on:click="spacingOpen = !spacingOpen"
            >
              <svg
                class="disclosure-caret"
                :class="{ open: spacingOpen }"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="currentColor"
              >
                <path d="M10 6l6 6-6 6z" />
              </svg>
              Spacing around the block
            </button>
          </div>
          <div class="col-12 mb-2" v-if="spacingOpen">
            <div class="hint-text">
              Empty space outside the block — top, bottom, left, right.
              “default” keeps the theme’s own spacing.
            </div>
          </div>
          <div class="col-6" v-if="spacingOpen">
            <div class="input-group">
              <label>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path
                    d="M21 3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h18zm-1 2H4v14h16V5zm-2 2v2H6V7h12z"
                  />
                </svg>
              </label>
              <select v-model="values['top']" v-on:change="changeBlockStyle()">
                <option
                  v-for="(name, key) in margin.top.classes"
                  :key="key"
                  :value="key"
                >
                  {{ name }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-6" v-if="spacingOpen">
            <div class="input-group">
              <label>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path
                    d="M21 3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h18zm-1 2H4v14h16V5zm-2 10v2H6v-2h12z"
                  />
                </svg>
              </label>
              <select
                v-model="values['bottom']"
                v-on:change="changeBlockStyle()"
              >
                <option
                  v-for="(name, key) in margin.bottom.classes"
                  :key="key"
                  :value="key"
                >
                  {{ name }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-6" v-if="spacingOpen">
            <div class="input-group">
              <label>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path
                    d="M21 3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h18zm-1 2H4v14h16V5zm-2 2v10h-2V7h2z"
                  />
                </svg>
              </label>
              <select v-model="values['left']" v-on:change="changeBlockStyle()">
                <option
                  v-for="(name, key) in margin.left.classes"
                  :key="key"
                  :value="key"
                >
                  {{ name }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-6" v-if="spacingOpen">
            <div class="input-group">
              <label>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path
                    d="M21 3a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h18zm-1 2H4v14h16V5zM8 7v10H6V7h2z"
                  />
                </svg>
              </label>
              <select
                v-model="values['right']"
                v-on:change="changeBlockStyle()"
              >
                <option
                  v-for="(name, key) in margin.right.classes"
                  :key="key"
                  :value="key"
                >
                  {{ name }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div
        class="white-box"
        v-for="block in selectedBlock?.type?.blocks?.filter(
          (block) => block.type === 'array'
        )"
        :key="block.key"
      >
        <div class="row align-items-center mb-2 gx-0">
          <div class="col">
            <h1>{{ block.name }}</h1>
          </div>
          <div class="col-auto">
            <button
              class="text-btn"
              title="Add item"
              v-on:click="addNewBlockItem(block)"
            >
              + Item
            </button>
          </div>
        </div>
        <template v-if="selectedBlock.value.settings[block.key]">
          <draggable
            tag="ul"
            class="drag-area"
            :list="selectedBlock.value.settings[block.key]"
            :group="{ name: 'cms-blocks' }"
            :item-key="itemKey"
            v-bind="dragOptions"
          >
            <template #item="{ element, index }">
              <li class="array-item">
                <!-- Top block-->
                <div class="array-item-row">
                  <img
                    v-if="itemThumb(element)"
                    class="array-item-thumb"
                    :src="itemThumb(element)"
                    alt=""
                    loading="lazy"
                    v-on:error="onItemThumbError($event, element)"
                  />
                  <span v-else class="array-item-thumb array-item-thumb-empty">
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="currentColor"
                    >
                      <path
                        d="M4 5h16v14H4V5zm2 2v10h12V7H6zm3 3a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm-2 5l3.5-4.5 2.5 3 1.5-2 3.5 3.5H7z"
                      />
                    </svg>
                  </span>
                  <span class="array-item-title">{{
                    getItemTitle(element, block, index)
                  }}</span>
                  <button
                    class="icon-btn icon-btn-accent"
                    title="Edit item"
                    @click="editContent(block.blocks, element)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="17"
                      height="17"
                      fill="currentColor"
                    >
                      <path
                        d="M15.728 9.686l-1.414-1.414L5 17.586V19h1.414l9.314-9.314zm1.414-1.414l1.414-1.414-1.414-1.414-1.414 1.414 1.414 1.414zM7.242 21H3v-4.243L16.435 3.322a1 1 0 0 1 1.414 0l2.829 2.829a1 1 0 0 1 0 1.414L7.243 21z"
                      />
                    </svg>
                  </button>
                  <button
                    class="icon-btn icon-btn-danger"
                    title="Delete item"
                    @click="deleteSubBlock(block.key, index)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="17"
                      height="17"
                      fill="currentColor"
                    >
                      <path
                        d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"
                      />
                    </svg>
                  </button>
                </div>

                <!-- Nested block -->
                <div
                  v-if="
                    block.blocks &&
                    block.blocks.findIndex((e) => e.type === 'array') > -1
                  "
                  class="nested-subgroup"
                >
                  <template
                    v-for="subBlock in block.blocks"
                    :key="subBlock.key"
                  >
                    <template v-if="subBlock.type === 'array'">
                      <div class="nested-head">
                        <span class="section-label">{{ subBlock.name }}</span>
                        <button
                          class="text-btn"
                          title="Add item"
                          @click="addNestedItem(block.key, index, subBlock)"
                        >
                          + Item
                        </button>
                      </div>
                      <draggable
                        tag="ul"
                        class="nested-drag"
                        :list="element[subBlock.key]"
                        :group="{ name: 'cms-blocks' }"
                        :item-key="itemKey"
                        v-bind="dragOptions"
                      >
                        <template #item="{ element: child, index: i }">
                          <li class="array-item-row nested-row">
                            <img
                              v-if="itemThumb(child)"
                              class="array-item-thumb small"
                              :src="itemThumb(child)"
                              alt=""
                              loading="lazy"
                            />
                            <span
                              v-else
                              class="array-item-thumb array-item-thumb-empty small"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                width="14"
                                height="14"
                                fill="currentColor"
                              >
                                <path
                                  d="M4 5h16v14H4V5zm2 2v10h12V7H6zm3 3a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm-2 5l3.5-4.5 2.5 3 1.5-2 3.5 3.5H7z"
                                />
                              </svg>
                            </span>
                            <span class="array-item-title">{{
                              getItemTitle(child, subBlock, i)
                            }}</span>
                            <div class="array-item-actions">
                              <button
                                class="icon-btn icon-btn-accent"
                                title="Edit item"
                                @click="editContent(subBlock.blocks, child)"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M15.728 9.686l-1.414-1.414L5 17.586V19h1.414l9.314-9.314zm1.414-1.414l1.414-1.414-1.414-1.414-1.414 1.414 1.414 1.414zM7.242 21H3v-4.243L16.435 3.322a1 1 0 0 1 1.414 0l2.829 2.829a1 1 0 0 1 0 1.414L7.243 21z"
                                  />
                                </svg>
                              </button>
                              <button
                                class="icon-btn icon-btn-danger"
                                @click="
                                  deleteNestedItem(
                                    block.key,
                                    index,
                                    subBlock.key,
                                    i
                                  )
                                "
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                >
                                  <path
                                    d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"
                                  />
                                </svg>
                              </button>
                            </div>
                          </li>
                        </template>
                      </draggable>
                    </template>
                  </template>
                </div>
              </li>
            </template>
          </draggable>
        </template>
      </div>
    </aside>
    <aside class="editor-right" v-else>
      <div class="white-box">
        <div class="empty-state">
          <div class="empty-icon">
            <svg viewBox="0 0 24 24">
              <path
                d="M15.728 9.686l-1.414-1.414L5 17.586V19h1.414l9.314-9.314zm1.414-1.414l1.414-1.414-1.414-1.414-1.414 1.414 1.414 1.414zM7.242 21H3v-4.243L16.435 3.322a1 1 0 0 1 1.414 0l2.829 2.829a1 1 0 0 1 0 1.414L7.243 21z"
              />
            </svg>
          </div>
          <div class="empty-title">Nothing selected</div>
          <div class="empty-text">
            Select a block in the list to edit its settings.
          </div>
        </div>
      </div>
    </aside>
  </div>
  <div
    class="drawer-backdrop"
    v-if="historyShow"
    v-on:click="historyShow = false"
  >
    <div class="drawer" v-on:click.stop>
      <div class="drawer-head">
        <h2>{{ $t("editor.version_history") }}</h2>
        <button class="icon-btn" title="Close" v-on:click="historyShow = false">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="currentColor"
          >
            <path
              d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"
            />
          </svg>
        </button>
      </div>
      <div class="drawer-body">
        <div class="version-row version-current" v-if="versions[0]?.current">
          <div class="version-info">
            <div class="version-label">Current</div>
            <div class="version-when">Newest and active</div>
          </div>
          <button
            class="btn btn-outline-primary btn-small newest-btn"
            v-if="viewedVersion !== null"
            v-on:click="returnToNewest"
          >
            {{ $t("editor.back_to_newest") }}
          </button>
          <span class="badge-soft badge-green" v-else>Current</span>
        </div>
        <div class="version-row" v-for="v in versions.slice(1)" :key="v.when">
          <div class="version-info">
            <div class="version-label">Version {{ v.num }}</div>
            <div class="version-when">{{ formatWhen(v.when) }}</div>
          </div>
          <button
            class="btn btn-outline-primary btn-small"
            v-on:click="restoreVersion(v)"
          >
            {{ $t("editor.restore") }}
          </button>
        </div>
        <div class="hint-text" v-if="versions.length === 0">
          {{ $t("editor.no_restore_points") }}
        </div>
        <div class="hint-text" v-else>
          Every Save creates a restore point. Restoring can be undone with
          Ctrl+Z.
        </div>
      </div>
    </div>
  </div>
  <div class="app-toast" v-if="toastMsg">
    <svg viewBox="0 0 24 24" width="16" height="16">
      <path
        d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-.997-6l7.07-7.071-1.414-1.414-5.656 5.657-2.829-2.829-1.414 1.414L11.003 16z"
      />
    </svg>
    {{ toastMsg }}
  </div>
  <AddBlock
    v-if="addBlockShow"
    v-model:show="addBlockShow"
    v-on:addBlock="addBlock"
    v-bind:template="template"
  />
  <EditContent
    v-if="editContentShow"
    v-model:show="editContentShow"
    v-model:block="selectedBlock"
    v-model:elements="editContentBlock"
    v-model:settings="editContentSettings"
    :variables="activePossibleVariables"
    :menus="menusData"
    v-on:menuChanged="menuChanged"
    :dynamic="activeDynamicBlocks"
    :languages="languages"
    :page-language="language"
    v-on:save="editContentChange"
  />
  <GlobalSettings
    v-if="globalShettingsShow"
    v-model:show="globalShettingsShow"
    v-model="globalVariables"
    v-on:change="changeStyle"
    v-on:importCss="addImportCss"
  />
  <AddPage
    v-if="addPageShow"
    v-model:show="addPageShow"
    v-model:page="selectedPage"
    v-model:webshops="webshops"
    :page-category="selectedPage ? categories[selectedPage.id] ?? '' : ''"
    :category-suggestions="allCategories"
    v-on:edit="editPage"
  />
</template>

<script>
import Twig from "twig";

import draggable from "vuedraggable";
import AddBlock from "../components/modals/AddBlock";
import AddPage from "../components/modals/AddPage";
import GlobalSettings from "../components/modals/GlobalSettings";
import HeaderBlock from "../components/header";
import http from "../modules/http";
import {
  blockIcon as getBlockIcon,
  registerTemplateIcons,
} from "../modules/block-icons";
import EditContent from "../components/modals/EditContent";
import ConditionGroupEditor from "../components/ConditionGroupEditor";
import { pageVariables, dynamicBlocks } from "../modules/page-variables";
import { fieldLabel } from "../modules/menu-field";
import { blockSeoGaps } from "../modules/seo-gaps";
import { recordProblem } from "../modules/problem-log";
import { smallVariant } from "../modules/media-size";

// Module-level caches that survive switching between pages — the editor
// would otherwise refetch ~70 twig files and ~20 translation files on every
// page open, which is slow and trips the API's rate limit (HTTP 429).
const twigFileCache = {};
const translationCache = {};
// Global dynamic blocks (e.g. the theme's header menu) are the same for every
// page, so render them once per template instead of on each page switch.
const globalBlockCache = {};
// Category names for the visibility rules, per language.
const conditionCategoryCache = {};

// The category endpoint answers with a tree; a rule compares one id, so the
// tree is flattened and each entry carries the path it sits on — two shops
// have a "Sale" under three different parents, and the name alone would be
// the same choice three times over.
function flattenCategories(categories, parentName = "") {
  const flat = [];
  for (const item of categories ?? []) {
    const name = parentName ? `${parentName} / ${item.name}` : item.name;
    flat.push({ id: item.id, name });
    if (item.categories !== undefined) {
      flat.push(...flattenCategories(item.categories, name));
    }
  }
  return flat;
}

// The product search API returns one hard-coded thumbnail path per image
// (product/439/fn-01/262-fn-01.webp), while the storefront's own renderer gets
// a full size map. Without reconstructing the variants every product image is
// a 262px thumbnail stretched to ~433px — visibly pixelated. Widths verified
// against the media server; the live site serves the same 582/720 variants.
// TODO(backend): return the sizes map for product images like cover images do,
// then these constants can go away.
import tenant from "../modules/tenant";
const MEDIA_BASE = tenant.getAppMediaUrl() + "/";
const IMAGE_WIDTHS = [262, 582, 720, 1440];
const SIZE_TO_WIDTH = { thumb: 262, small: 262, medium: 720, large: 1440 };

// "dir/262-name.webp" -> { dir, file }; null when the path isn't width-prefixed
function splitImagePath(path) {
  const match = String(path ?? "").match(/^(.*\/)(\d+)-(.+)$/);
  return match ? { dir: match[1], file: match[3] } : null;
}

function imageVariant(path, width) {
  const parts = splitImagePath(path);
  return parts ? `${MEDIA_BASE}${parts.dir}${width}-${parts.file}` : null;
}

function imageSrcset(path) {
  const parts = splitImagePath(path);
  if (!parts) {
    return "";
  }
  return IMAGE_WIDTHS.map(
    (w) => `${MEDIA_BASE}${parts.dir}${w}-${parts.file} ${w}w`
  ).join(", ");
}
// The editor API rate-limits hard (~60 req/min, HTTP 429). Pages with many
// product blocks fire one search per block on every render — cache the
// in-flight promise per parameter set so identical searches hit the API once.
const productSearchCache = {};

export default {
  name: "BlockEditor",
  components: {
    AddPage,
    EditContent,
    HeaderBlock,
    GlobalSettings,
    AddBlock,
    draggable,
    ConditionGroupEditor,
  },
  watch: {
    language(value, old) {
      if (
        value !== null &&
        old !== null &&
        value !== undefined &&
        old !== undefined
      ) {
        (async () => {
          await Promise.all([this.loadBlocks(), this.loadTranslations()]);
          this.loadJson();
        })();
      }
    },
    size() {
      this.$nextTick(() => {
        this.calcRate();
        clearTimeout(this.rateTimer);
        this.rateTimer = setTimeout(() => this.calcRate(), 400);
      });
    },
    previewShop(value, old) {
      if (
        value !== null &&
        old !== null &&
        value !== undefined &&
        old !== undefined
      ) {
        // re-render so the twig shop context (currency, domain, name)
        // matches the selected webshop
        this.loadJson();
      }
    },
  },
  data() {
    return {
      template: undefined,
      size: "wide",
      phoneTime: "",
      clockInterval: null,
      rateTimer: null,
      rate: 1,
      previewShop: null,
      // The page records that share this page's slug — its region variants.
      // They are separate records so a region can be built freely, but the
      // editor presents them as one page you switch between.
      regionVariants: [],
      regionChoice: null,
      switchingRegion: false,
      regionMenuOpen: false,
      dragOptions: {
        animation: 200,
      },
      language: null,
      activePage: {
        id: this.$route.params.id,
        language: "",
      },
      languages: [],
      zoom: undefined,
      height: undefined,
      values: {},
      content: {},
      pageJson: null,
      contentForLayout: null,
      // rendered HTML of the theme's global dynamic blocks, keyed by the twig
      // variable name they fill (e.g. content_for_infobox)
      globalBlocks: {},
      generatedCss: null,
      loaded: {
        script: false,
        style: false,
        page: false,
        content: false,
        var: 0,
        // progress is rendered reactively from loaded.var; kept as a no-op
        // so the many .bar.set() call sites stay untouched
        bar: { set() {} },
      },
      addBlockShow: false,
      insertIndex: null,
      historyShow: false,
      viewedVersion: 0,
      versions: [],
      expandedBlocks: {},
      globalShettingsShow: false,
      editContentShow: false,
      editContentBlock: null,
      editContentSettings: null,
      globalVariables: [],
      editBlockName: false,
      selectedBlock: undefined,
      undoStack: [],
      redoStack: [],
      dirty: false,
      saveStatus: "",
      saving: false,
      toastMsg: null,
      toastTimeout: null,
      spacingOpen: false,
      margin: {
        top: {
          classes: {
            "": "default",
            "mt-0": "0x",
            "mt-1": "1x",
            "mt-2": "2x",
            "mt-3": "3x",
            "mt-4": "4x",
            "mt-5": "5x",
          },
        },
        bottom: {
          classes: {
            "": "default",
            "mb-0": "0x",
            "mb-1": "1x",
            "mb-2": "2x",
            "mb-3": "3x",
            "mb-4": "4x",
            "mb-5": "5x",
          },
        },
        left: {
          classes: {
            "": "default",
            "ml-0": "0x",
            "ml-1": "1x",
            "ml-2": "2x",
            "ml-3": "3x",
            "ml-4": "4x",
            "ml-5": "5x",
          },
        },
        right: {
          classes: {
            "": "default",
            "mr-0": "0x",
            "mr-1": "1x",
            "mr-2": "2x",
            "mr-3": "3x",
            "mr-4": "4x",
            "mr-5": "5x",
          },
        },
      },
      blocks: [],
      files: {},
      translations: {},
      menusData: [],
      webshops: [],
      addPageShow: false,
      selectedPage: null,
      categories: {},
      jsonUnderLoad: false,
      possibleBlocks: [],
      interval: null,
      // Which paths a page type offers — and the name each is shown under —
      // lives in modules/page-variables.js. The lists a rule's value can be
      // picked from are loaded below.
      conditionCategories: [],
    };
  },
  computed: {
    // How many blocks on this page still have an empty visible field, so the
    // count is readable without opening every block in turn.
    blocksWithGaps() {
      return this.blocks.filter((block) => this.blockGaps(block).length > 0)
        .length;
    },
    languageName() {
      return (
        this.languages?.find((lang) => lang.code === this.language)?.name ??
        null
      );
    },
    previewDomain() {
      if (
        this.activePage?.slug !== undefined &&
        this.activePage?.slug !== null
      ) {
        return "/" + this.activePage.slug;
      }
      return this.activePage?.name ?? "preview";
    },
    activePossibleVariables() {
      if (this.activePage?.page_type !== null) {
        return pageVariables(this.activePage?.page_type);
      }
      return [];
    },
    liveDomain() {
      const shop = this.activePage?.webshop_id
        ? this.webshops.find((w) => w.id === this.activePage?.webshop_id)
        : this.webshops.find((w) => w.is_default) ?? this.webshops[0];
      return shop?.domain;
    },
    // The versions of this page that actually exist: the All webshops page
    // and any region carved out of it. Regions that were never carved out are
    // not listed — there is nothing to switch to, they are the All webshops
    // page.
    existingRegions() {
      const list = [];
      if (this.variantFor(null)) {
        list.push({ key: "", label: "All webshops" });
      }
      for (const shop of this.webshops ?? []) {
        if (this.variantFor(shop.id)) {
          list.push({ key: String(shop.id), label: shop.name });
        }
      }
      return list;
    },
    addableRegions() {
      return (this.webshops ?? []).filter((shop) => !this.variantFor(shop.id));
    },
    activeDynamicBlocks() {
      if (this.activePage?.page_type !== null) {
        return dynamicBlocks(this.activePage?.page_type);
      }
      return [];
    },
    allCategories() {
      const values = new Set();
      for (const category of Object.values(this.categories)) {
        if (category) {
          values.add(category);
        }
      }
      return [...values].sort();
    },
  },
  mounted() {
    this.updatePhoneTime();
    this.clockInterval = setInterval(this.updatePhoneTime, 1000);
    http.fetch(`/themes`).then((data) => {
      this.template = data.data[0].id;
      this.templates = data.data;
      this.webshops = data.webshops ?? [];
      const defaultShop =
        this.webshops.find((w) => w.is_default) ?? this.webshops[0];
      this.previewShop = defaultShop?.id ?? null;
      if (!twigFileCache[this.template]) {
        twigFileCache[this.template] = {};
      }
      this.files = twigFileCache[this.template];

      this.loadPageRecord();
    });

    if (this.languages.length < 1) {
      http.fetch("/languages").then((data) => {
        this.languages = data;
        if (this.language === null || this.language?.length !== 2) {
          // The API lists languages alphabetically, so falling back to the
          // first one silently opened the editor in Czech — and everything
          // typed went into the Czech slot. That is how the Romanian legal
          // pages ended up with their text filed under "cs", invisible when
          // editing in Romanian. Prefer English, which at least matches how
          // most pages here are authored, and never pick by list order.
          this.language =
            data.find((lang) => lang.code === "en")?.code ?? data[0]?.code;
        }
      });
    }

    window.addEventListener("resize", this.getDimensions);
    window.addEventListener("keydown", this.keyHandler);
    window.addEventListener("beforeunload", this.beforeUnloadHandler);
    this.loadCategories();
  },
  unmounted() {
    window.removeEventListener("resize", this.getDimensions);
    window.removeEventListener("keydown", this.keyHandler);
    window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
    if (this.rateTimer) {
      clearTimeout(this.rateTimer);
    }
  },
  beforeRouteLeave(to, from, next) {
    if (
      this.dirty &&
      !window.confirm(
        "You have unpublished changes. Leave without saving them?"
      )
    ) {
      next(false);
      return;
    }
    next();
  },
  methods: {
    updatePhoneTime() {
      this.phoneTime = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    loadCategories() {
      http.fetch("/page").then((data) => {
        this.categories = Object.fromEntries(
          (data.data ?? [])
            .filter((p) => p.category)
            .map((p) => [p.id, p.category])
        );
      });
    },
    showEditPage() {
      http.fetch("/page/" + this.activePage.id).then((data) => {
        this.selectedPage = data.data;
        this.addPageShow = true;
      });
    },
    editPage(page) {
      http
        .fetch("/page/" + page.id, page)
        .then(() => {
          if (page.category) {
            this.categories[page.id] = page.category;
          } else {
            delete this.categories[page.id];
          }
          if (this.activePage.id === page.id) {
            this.activePage.name = page.name;
            this.activePage.slug = page.slug;
            this.activePage.title = page.title;
          }
          this.addPageShow = false;
        })
        .catch((err) => {
          const fields = err?.errors ?? {};
          const detail = Object.values(fields).flat().join("\n");
          window.alert(
            "The changes could not be saved.\n\n" +
              (detail || err?.message || "Could not save the page.")
          );
        });
    },
    // A style option's "classes" is a map of css-class -> label. An unset
    // setting matched no option, which is why these dropdowns rendered blank.
    // Where the template offers no empty-string choice there is nothing to fall
    // back to, so a "Default" entry is added for it.
    // ---- Region variants (one page, edited region by region) ----
    // Every variant answers the same /<lang>/<region>/<slug> address; the
    // slug is what ties them together. A region without its own record is
    // served the All webshops page, which is why "no own content" is a
    // normal state rather than something missing.
    loadRegionVariants() {
      if (!this.activePage?.slug) {
        return;
      }
      http
        .fetch("/page")
        .then((data) => {
          this.regionVariants = (data.data ?? []).filter(
            (page) => page.slug === this.activePage.slug
          );
        })
        .catch(() => {
          // Not being able to list them only costs the switcher its hints.
          this.regionVariants = [];
        });
    },
    // Loads everything that belongs to one page record. Split out of mounted()
    // so switching region can reuse it: /editor/:id keeps the same component
    // alive, and reloading the whole app for a switch threw the user back to
    // the sign-in screen every time.
    loadPageRecord() {
      return http.fetch(`/page/${this.activePage.id}`).then((data) => {
        this.activePage = data.data;
        this.language = data.data.language_code;
        // The region switcher shows the region this record belongs to, and
        // the preview follows it, so what you edit and what you see match.
        this.regionChoice = data.data.webshop_id ?? null;
        if (data.data.webshop_id) {
          this.previewShop = data.data.webshop_id;
        }
        this.loadRegionVariants();

        this.pageJson = data.data.json;
        this.loadVersions();

        (async () => {
          this.loaded.var = 10;
          this.loaded.bar.set(this.loaded.var);

          await Promise.all([
            this.loadBlocks(),
            this.loadTranslations(),
            this.loadMenus(),
            this.loadConditionCategories(),
          ]);
          await this.initTwig();
          await this.loadJson();
        })();

        this.activePage.styleFormat = "sass"; // TODO
        if (this.activePage.styleFormat === "sass") {
          this.generateSass();
        } else {
          this.generatedCss = null;
          this.loaded.style = true;
          this.loaded.var += 20;
          this.loaded.bar.set(this.loaded.var);
          this.setScript();
        }
      });
    },
    // Switching to another region's record. The twig, sass and translation
    // caches are keyed by template, not by page, so they stay valid — only
    // the page-specific state has to be cleared.
    openPage(id) {
      this.switchingRegion = true;
      this.dirty = false; // already confirmed above; don't prompt again
      this.selectedBlock = undefined;
      this.blocks = [];
      this.pageJson = null;
      this.contentForLayout = "";
      this.undoStack = [];
      this.redoStack = [];
      this.versions = [];
      this.expandedBlocks = {};
      this.saveStatus = "";
      this.loaded.content = false;
      this.loaded.page = false;
      this.activePage = { id: String(id), language: "" };
      this.$router.push("/editor/" + id);
      this.loadPageRecord().finally(() => {
        this.switchingRegion = false;
      });
    },
    variantFor(webshopId) {
      return this.regionVariants.find(
        (page) => (page.webshop_id ?? null) === (webshopId ?? null)
      );
    },
    onRegionChange(raw) {
      const webshopId = raw === "" ? null : Number(raw);
      if (webshopId === (this.regionChoice ?? null) || this.switchingRegion) {
        return;
      }
      const variant = this.variantFor(webshopId);
      if (!variant) {
        return; // only versions that exist are offered
      }
      if (
        this.dirty &&
        !window.confirm(
          "You have unpublished changes. Leave them and switch version?"
        )
      ) {
        return;
      }
      this.openPage(variant.id);
    },
    // Carving a region out of the All webshops page: from here on that region
    // is edited on its own, while the All webshops page keeps serving the rest.
    async addRegion(shop) {
      this.regionMenuOpen = false;
      if (
        !window.confirm(
          `${shop.name} currently shows the All webshops page.\n\n` +
            `Give ${shop.name} its own version, starting as a copy of this one? ` +
            `The other regions keep using the All webshops page.`
        )
      ) {
        return;
      }
      await this.createRegionVariant(shop.id, shop.name);
    },
    async createRegionVariant(webshopId, name) {
      if (this.dirty) {
        window.alert(
          "Save this page first — the new region starts as a copy of it."
        );
        return;
      }
      this.switchingRegion = true;
      try {
        const created = await http.fetch("/page", {
          name: this.activePage.name,
          slug: this.activePage.slug,
          title: this.activePage.title ?? "",
          description: "",
          language_code: this.activePage.language_code || this.language,
          webshop_id: webshopId ?? "",
        });
        const newId = created?.data?.id;
        if (!newId) {
          throw new Error("Could not create the page.");
        }
        await http.fetch(
          `/page/${newId}`,
          {
            json: JSON.stringify(this.pageJson ?? { sections: {}, order: [] }),
          },
          false,
          "PUT"
        );
        // Match the source page's state: a copy that is live from the start
        // changes nothing for visitors until it is actually edited.
        if (this.activePage.is_active) {
          await http.fetch(`/page/${newId}/status`, { is_active: 1 });
        }
        this.openPage(newId);
      } catch (err) {
        this.switchingRegion = false;
        const fields = err?.errors ?? {};
        const detail = Object.values(fields).flat().join("\n");
        window.alert(
          `Could not create the ${name} page.\n\n` +
            (detail || err?.message || "Could not save the page.")
        );
      }
    },
    styleOptions(style) {
      const classes = style?.classes ?? {};
      const options = Object.entries(classes).map(([value, label]) => ({
        value,
        label,
      }));
      if (!Object.prototype.hasOwnProperty.call(classes, "")) {
        options.unshift({ value: "", label: "Default" });
      }
      return options;
    },
    styleValue(style) {
      return this.selectedBlock?.value?.settings?.[style.key] ?? "";
    },
    setStyleValue(style, value) {
      if (!this.selectedBlock?.value?.settings) {
        return;
      }
      this.selectedBlock.value.settings[style.key] = value;
      this.changeBlockStyle();
    },
    // "Visible at" support: templates hide blocks per device with
    // framework-specific classes. The template's own blocks.json schema is
    // the source of truth — classify by its option labels first, and only
    // fall back to recognising well-known utility classes.
    blockVisibility(block) {
      const settings = block?.value?.settings ?? {};
      for (const sub of block?.type?.blocks ?? []) {
        for (const style of sub?.styles ?? []) {
          if (!style?.classes || !style.key) {
            continue;
          }
          const labels = Object.values(style.classes).join(" ").toLowerCase();
          if (!/desktop/.test(labels) || !/phone|mobile/.test(labels)) {
            continue;
          }
          const label = String(
            style.classes[settings[style.key]] ?? ""
          ).toLowerCase();
          if (/desktop/.test(label)) {
            return "desktop";
          }
          if (/phone|mobile/.test(label)) {
            return "phone";
          }
        }
      }
      for (const value of Object.values(settings)) {
        const visibility = this.visibilityFromClasses(value);
        if (visibility) {
          return visibility;
        }
      }
      return null;
    },
    // Recognise device-visibility utility classes from Bootstrap, Tailwind
    // and custom (e.g. PureCSS-based) templates without schema help.
    visibilityFromClasses(value) {
      if (typeof value !== "string" || value.trim() === "") {
        return null;
      }
      const tokens = value.trim().toLowerCase().split(/\s+/);
      const has = (re) => tokens.some((t) => re.test(t));
      const hiddenBase = tokens.includes("d-none") || tokens.includes("hidden");
      const shownFromBreakpoint =
        has(
          /^d-(sm|md|lg|xl|xxl)-(block|flex|grid|inline|inline-block|inline-flex|table)$/
        ) ||
        has(
          /^(sm|md|lg|xl|2xl):(block|flex|grid|inline|inline-block|inline-flex|table|contents)$/
        );
      if (hiddenBase && shownFromBreakpoint) {
        return "desktop";
      }
      if (has(/^max-(sm|md|lg|xl|2xl):hidden$/)) {
        return "desktop";
      }
      if (
        !hiddenBase &&
        (has(/^d-(sm|md|lg|xl|xxl)-none$/) || has(/^(sm|md|lg|xl|2xl):hidden$/))
      ) {
        return "phone";
      }
      const joined = ` ${tokens.join(" ")} `;
      if (
        /(desktop-only|only-desktop|hide-(on-)?(mobile|phone)|hidden-(on-)?(mobile|phone))/.test(
          joined
        )
      ) {
        return "desktop";
      }
      if (
        /((mobile|phone)-only|only-(mobile|phone)|hide-(on-)?desktop|hidden-(on-)?desktop)/.test(
          joined
        )
      ) {
        return "phone";
      }
      return null;
    },
    // A block's missing alt texts and empty media fields, by their own labels.
    // Translated fields are read in the language being edited, so switching
    // the language switches what counts as missing.
    blockGaps(block) {
      return blockSeoGaps(
        block?.type?.blocks,
        block?.value?.settings ?? {},
        this.language
      ).map((gap) => gap.name);
    },
    blockGapTitle(block) {
      const gaps = this.blockGaps(block);
      if (gaps.length === 0) {
        return null;
      }
      return `Missing on this page: ${gaps.join(", ")} — click to fill in`;
    },
    isHiddenOnCurrentDevice(block) {
      const visibility = this.blockVisibility(block);
      if (visibility === "phone") {
        return this.size !== "phone";
      }
      if (visibility === "desktop") {
        return this.size === "phone";
      }
      return false;
    },
    // Stable keys for array items that have no natural id — undefined keys
    // (the old item-key="name") crash Vue's patching with
    // "Cannot read properties of null (reading 'emitsOptions')".
    itemKey(element) {
      if (element === null || typeof element !== "object") {
        return String(element);
      }
      if (!this._itemKeys) {
        this._itemKeys = new WeakMap();
        this._itemKeySeq = 0;
      }
      if (!this._itemKeys.has(element)) {
        this._itemKeySeq += 1;
        this._itemKeys.set(element, "item-" + this._itemKeySeq);
      }
      return this._itemKeys.get(element);
    },
    blockIcon(typeName, fallback = null) {
      return getBlockIcon(typeName, fallback);
    },
    beforeUnloadHandler(e) {
      if (this.dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    },
    blockProducts(block) {
      const products = block?.value?.products;
      const list = Array.isArray(products) ? products : products?.data;
      return Array.isArray(list) ? list : [];
    },
    toggleExpand(key) {
      this.expandedBlocks[key] = !this.expandedBlocks[key];
    },
    productImage(product) {
      const image = product?.image;
      if (!image) {
        return "";
      }
      if (String(image).startsWith("http")) {
        return image;
      }
      return `${process.env.VUE_APP_MEDIA_URL}/${image}`;
    },
    formatPrice(price) {
      if (price === null || price === undefined) {
        return "";
      }
      if (typeof price === "object") {
        return `${price.amount} ${price.currency}`;
      }
      return String(price);
    },
    isImagePath(value) {
      return (
        typeof value === "string" &&
        /\.(webp|png|jpe?g|gif|avif|svg)(\?.*)?$/i.test(value)
      );
    },
    // Find the first image-looking value inside an array item so the
    // Slides/Items panel can show a thumbnail.
    // `full` asks for the stored picture itself; by default this hands back a
    // small render of it, because the thumbnail is 40px wide and the original
    // is routinely 3000px and several megabytes. A slider with ten slides was
    // downloading and decoding tens of megabytes to draw a column of icons.
    itemThumb(element, full = false) {
      if (!element || typeof element !== "object") {
        return null;
      }
      const found = this.findImageIn(element, 2);
      if (!found) {
        return null;
      }
      // Stored values come both ways — a path relative to the media server and
      // a full URL — and both point at the original file, so both need the
      // small render.
      if (found.startsWith("http")) {
        return full ? found : smallVariant(found);
      }
      const path = full ? found : smallVariant(found);
      return `${process.env.VUE_APP_MEDIA_URL}/${path}`;
    },
    // Not every picture has every render on the media server. When the small
    // one is missing the original still shows, so a thumbnail is never broken.
    onItemThumbError(event, element) {
      const original = this.itemThumb(element, true);
      if (original && event?.target && event.target.src !== original) {
        event.target.src = original;
      }
    },
    findImageIn(value, depth) {
      if (depth < 0 || value === null || value === undefined) {
        return null;
      }
      if (this.isImagePath(value)) {
        return value;
      }
      if (typeof value === "object") {
        if (this.isImagePath(value.src)) {
          return value.src;
        }
        for (const key of Object.keys(value)) {
          const hit = this.findImageIn(value[key], depth - 1);
          if (hit) {
            return hit;
          }
        }
      }
      return null;
    },
    getItemTitle(element, block, index) {
      const main = this.getSectionTitle(element, block);
      if (typeof main === "string" && main.trim().length > 0) {
        return main;
      }
      for (const key of ["title", "name", "heading", "label", "text"]) {
        const resolved = this.resolveText(element?.[key]);
        if (resolved) {
          return resolved;
        }
      }
      return `Item ${index + 1}`;
    },
    resolveText(value) {
      if (typeof value === "string") {
        const plain = value.replace(/<[^>]*>/g, "").trim();
        return plain.length > 0 ? plain : null;
      }
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const byLang = value[this.language] ?? value[Object.keys(value)[0]];
        return this.resolveText(byLang);
      }
      return null;
    },
    keyHandler(e) {
      if (!(e.ctrlKey || e.metaKey)) {
        return;
      }
      const target = e.target;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        this.undo();
      } else if (key === "y" || (key === "z" && e.shiftKey)) {
        e.preventDefault();
        this.redo();
      }
    },
    toast(msg) {
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout);
      }
      this.toastMsg = msg;
      this.toastTimeout = setTimeout(() => {
        this.toastMsg = null;
      }, 2200);
    },
    pushHistory() {
      if (this.pageJson === null) {
        return;
      }
      this.undoStack.push(JSON.stringify(this.pageJson));
      if (this.undoStack.length > 30) {
        this.undoStack.shift();
      }
      this.redoStack = [];
      this.dirty = true;
      this.saveStatus = "";
    },
    async undo() {
      if (this.undoStack.length === 0 || this.jsonUnderLoad) {
        return;
      }
      this.redoStack.push(JSON.stringify(this.pageJson));
      this.pageJson = JSON.parse(this.undoStack.pop());
      this.selectedBlock = undefined;
      this.dirty = true;
      await this.loadJson();
    },
    async redo() {
      if (this.redoStack.length === 0 || this.jsonUnderLoad) {
        return;
      }
      this.undoStack.push(JSON.stringify(this.pageJson));
      this.pageJson = JSON.parse(this.redoStack.pop());
      this.selectedBlock = undefined;
      this.dirty = true;
      await this.loadJson();
    },
    startEditBlockName() {
      this.pushHistory();
      this.editBlockName = true;
    },
    toggleHidden(block) {
      this.pushHistory();
      if (block.value.hidden === true) {
        delete block.value.hidden;
      } else {
        block.value.hidden = true;
      }
      this.applyHidden();
    },
    applyHidden() {
      const doc = this.$refs.preview?.contentWindow?.document;
      if (!doc) {
        return;
      }
      for (const block of this.blocks) {
        const el = doc.querySelector(`[data-block_id="${block.key}"]`);
        if (el !== undefined && el !== null) {
          el.style.display = block.value.hidden === true ? "none" : "";
        }
      }
    },
    async duplicateBlock() {
      if (!this.selectedBlock) {
        return;
      }
      this.pushHistory();
      const sourceKey = this.selectedBlock.key;
      const copy = JSON.parse(
        JSON.stringify(this.pageJson.sections[sourceKey])
      );
      delete copy.products;
      const newKey = copy.type + this.generateID();
      this.pageJson.sections[newKey] = copy;
      const i = this.pageJson.order.findIndex((e) => e === sourceKey);
      this.pageJson.order.splice(i + 1, 0, newKey);
      this.selectedBlock = undefined;
      await this.loadJson();
      this.toast("Block duplicated");
    },
    addNestedItem(parentKey, parentIndex, subBlock) {
      this.pushHistory();
      if (
        this.selectedBlock.value.settings[parentKey][parentIndex] === undefined
      ) {
        this.selectedBlock.value.settings[parentKey][parentIndex] = {};
      }
      if (
        this.selectedBlock.value.settings[parentKey][parentIndex][
          subBlock.key
        ] === undefined
      ) {
        this.selectedBlock.value.settings[parentKey][parentIndex][
          subBlock.key
        ] = [];
      }
      this.selectedBlock.value.settings[parentKey][parentIndex][
        subBlock.key
      ].push({});
    },

    deleteNestedItem(parentKey, parentIndex, subKey, subIndex) {
      this.pushHistory();
      this.selectedBlock.value.settings[parentKey][parentIndex][subKey].splice(
        subIndex,
        1
      );
    },
    async loadProducts(section) {
      let searchParams = {
        status: [1],
      };

      if (typeof section.settings.featured_category === "object") {
        searchParams["category_id"] = section.settings.featured_category;
      }
      if (
        typeof section.settings.tags === "object" &&
        section.settings.tags !== null &&
        section.settings.tags?.length > 0
      ) {
        searchParams["tags"] = section.settings.tags;
      }
      if (
        typeof section.settings.characteristics === "object" &&
        section.settings.characteristics !== null
      ) {
        searchParams["characteristics"] = section.settings.characteristics;
      }
      if (typeof section.settings.featured_products === "object") {
        searchParams["variant_id"] = section.settings.featured_products;
      }
      if (typeof section.settings.featured_bundles === "object") {
        searchParams["bundle_id"] = section.settings.featured_bundles;
      }

      // Ask for prices in the previewed webshop's currency so the preview
      // matches what that store's visitors see (shop names double as
      // currency codes when no explicit currency field exists).
      const shop = this.webshops.find((w) => w.id === this.previewShop);
      const currency =
        shop?.currency ??
        (/^[A-Z]{3}$/.test(shop?.name ?? "") ? shop.name : null);
      if (currency) {
        searchParams["currency"] = currency;
      }

      const cacheKey = JSON.stringify(searchParams);
      if (!productSearchCache[cacheKey]) {
        productSearchCache[cacheKey] = http
          .fetch("/products/products/search", searchParams)
          .catch((e) => {
            // don't cache failures — the next render should retry
            delete productSearchCache[cacheKey];
            throw e;
          });
      }
      return await productSearchCache[cacheKey];
    },
    toggleStyle(style) {
      this.pushHistory();
      const key = style.key;
      const current = this.selectedBlock.value.settings[key];
      if (current === style.class) {
        delete this.selectedBlock.value.settings[key];
      } else {
        this.selectedBlock.value.settings[key] = style.class;
      }
      this.changeBlockStyle(false);
    },
    async changeBlockStyle(withHistory = true) {
      if (withHistory) {
        this.pushHistory();
      }
      this.selectedBlock.value.settings.margin = this.serializeMarginObject(
        this.values
      );
      const section = this.selectedBlock.value;
      const context = {
        settings: section.settings || {},
        section,
      };
      let upgraded = this.reloadBlock(
        "sections/" + this.selectedBlock.typeName,
        context,
        this.selectedBlock.key
      );
      if (!upgraded) {
        await this.loadBlocks();
        await this.loadJson();
      }
    },
    async editContentChange(settings) {
      this.pushHistory();
      Object.assign(this.editContentSettings, settings);

      const section = this.selectedBlock.value;

      if (
        this.selectedBlock.type?.type === "featured-products" &&
        (section.settings.featured_products === undefined ||
          section.settings.featured_bundles === undefined)
      ) {
        try {
          section.products = await this.loadProducts(section);
        } catch (e) {
          console.warn("Product search failed", e);
        }
      }

      const context = {
        settings: section.settings || {},
        section,
      };

      let upgraded = this.reloadBlock(
        "sections/" + this.selectedBlock.typeName,
        context,
        this.selectedBlock.key
      );
      if (!upgraded) {
        await this.loadBlocks();
        await this.loadJson();
      }
    },
    parseMarginString(str, config) {
      const result = {
        top: "",
        bottom: "",
        left: "",
        right: "",
      };
      if (typeof str !== "string" || !str.trim()) return result;

      const parts = str.trim().split(/\s+/);
      for (const part of parts) {
        for (const side of ["top", "bottom", "left", "right"]) {
          if (part in config[side].classes) {
            result[side] = part;
            break;
          }
        }
      }
      return result;
    },
    serializeMarginObject(obj) {
      const parts = [];
      if (obj.top) parts.push(obj.top);
      if (obj.right) parts.push(obj.right);
      if (obj.bottom) parts.push(obj.bottom);
      if (obj.left) parts.push(obj.left);
      return parts.join(" ");
    },
    // toggle is set by the two click paths (block list, preview). Clicking the
    // already-selected block clears the selection, so the right panel closes
    // and the preview stops scrolling the block back into view on every click.
    // previewAction must not toggle: it selects a block in order to act on it.
    showEditBlock(block, toggle = false) {
      if (toggle && this.selectedBlock?.key === block?.key) {
        this.selectedBlock = null;
        this.editBlockName = false;
        this.applySelectionHighlight();
        return;
      }
      this.selectedBlock = block;
      this.editBlockName = false;
      if (typeof this.selectedBlock?.value.settings.margin === "string") {
        this.values = this.parseMarginString(
          this.selectedBlock?.value.settings["margin"],
          this.margin
        );
      } else {
        this.values = { top: "", bottom: "", left: "", right: "" };
      }
      this.applySelectionHighlight(true);
    },
    applySelectionHighlight(scroll = false) {
      const doc = this.$refs.preview?.contentWindow?.document;
      if (!doc || !doc.body) {
        return;
      }
      doc
        .querySelectorAll(".editor-selected")
        .forEach((e) => e.classList.remove("editor-selected"));
      if (this.selectedBlock) {
        const el = doc.querySelector(
          `[data-block_id="${this.selectedBlock.key}"]`
        );
        if (el) {
          el.classList.add("editor-selected");
          const label = el.querySelector(":scope > .editor-label");
          if (label) {
            label.textContent =
              this.selectedBlock.value?.block_name ??
              this.selectedBlock.type?.name ??
              this.selectedBlock.key;
          }
          if (scroll) {
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        }
      }
    },
    openAddBlock(index = null) {
      this.insertIndex = index;
      this.addBlockShow = true;
    },
    setupPreviewInteractions() {
      const doc = this.$refs.preview?.contentWindow?.document;
      if (!doc || !doc.body) {
        return;
      }
      if (!doc.querySelector("#editor-ui-css")) {
        const style = doc.createElement("style");
        style.id = "editor-ui-css";
        style.innerHTML = `
          /* the theme's cart button is a Vue widget hidden by v-cloak until
             the storefront app mounts — show only its static bag icon in the
             preview, never the un-mounted dropdown with raw [[...]] bindings */
          /* font-size 0 also swallows the theme's stray "[[newItem]]" text
             node (header-cart.twig line 66) that Vue would consume on the
             live site */
          /* vertical-align must be set too: font-size:0 collapses the
             baseline, which would drag the bag icon above its sibling
             account icon (they sit ~3px apart on the live site) */
          #header-cart-container[v-cloak] { display: inline-block !important; font-size: 0 !important; vertical-align: middle !important; }
          #header-cart-container[v-cloak] #header-cart > button { font-size: 17px !important; }
          #header-cart-container[v-cloak] .header-cart-box { display: none !important; }
          #header-cart-container[v-cloak] #header-cart > :not(button) { display: none !important; }
          #header-cart-container[v-cloak] > :not(#header-cart) { display: none !important; }
          .editor-ring { position: absolute; inset: 0; pointer-events: none; z-index: 2147482997; border: 2px solid transparent; border-radius: 10px; transition: border-color .12s; }
          [data-block_id]:hover > .editor-ring { border-color: #c0bdbd; }
          [data-block_id].editor-selected > .editor-ring { border-color: #7c3aed !important; }
          .editor-label { display: none; position: absolute; top: 8px; left: 8px; z-index: 2147482998; background: #7c3aed; color: #fff; font-family: sans-serif; font-size: 10px; font-weight: 700; letter-spacing: .4px; text-transform: uppercase; padding: 3px 9px; border-radius: 6px; pointer-events: none; }
          [data-block_id].editor-selected > .editor-label { display: block; }
          .editor-block-tools { position: absolute; top: 8px; right: 8px; z-index: 2147483000; display: none; gap: 1px; background: #fff; border: 1px solid #E2E8F0; border-radius: 99px; padding: 3px; box-shadow: 0 4px 16px rgba(18,18,18,.14); }
          [data-block_id]:hover > .editor-block-tools { display: flex; }
          .editor-block-tools button { border: none; cursor: pointer; background: transparent; color: #505354; width: 26px; height: 26px; border-radius: 99px; display: grid; place-items: center; padding: 0; }
          .editor-block-tools button:hover { background: #F1F5F9; color: #7c3aed; }
          .editor-block-tools button.danger:hover { background: #facece; color: #C53030; }
          .editor-insert-zone { position: absolute; top: -20px; left: 0; right: 0; height: 40px; z-index: 2147482999; display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity .15s; }
          .editor-insert-zone:hover { opacity: 1; }
          .editor-insert-zone .editor-insert-line { position: absolute; left: 16px; right: 16px; top: 19px; height: 2px; background: #7c3aed; border-radius: 2px; }
          .editor-insert-zone .editor-insert-pill { position: relative; background: #7c3aed; color: #fff; border-radius: 99px; padding: 3px 12px; font-size: 11px; font-weight: 700; font-family: sans-serif; cursor: pointer; box-shadow: 0 2px 10px rgba(18,18,18,.3); }
          /* A block with no content of its own renders as blank space, which
             is indistinguishable from the block not being there at all. Mark
             it with the crossed-out box used for an empty slot. */
          [data-block_id].editor-empty { min-height: 170px; }
          .editor-empty-state { position: absolute; inset: 0; z-index: 2147482996; pointer-events: none; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; text-align: center; font-family: sans-serif; border: 1.5px dashed #cbd5e1; border-radius: 10px; background-color: #f8fafc; background-image: linear-gradient(to top right, transparent calc(50% - 1px), #e5e9f0 calc(50% - 1px), #e5e9f0 calc(50% + 1px), transparent calc(50% + 1px)), linear-gradient(to bottom right, transparent calc(50% - 1px), #e5e9f0 calc(50% - 1px), #e5e9f0 calc(50% + 1px), transparent calc(50% + 1px)); }
          .editor-empty-title { font-size: 13px; font-weight: 700; color: #475569; background: #f8fafc; padding: 0 10px; }
          .editor-empty-sub { font-size: 11px; font-weight: 600; color: #94a3b8; background: #f8fafc; padding: 0 10px; }
        `;
        doc.head.appendChild(style);
      }
      const icons = {
        up: '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 4l6 6-1.4 1.4L13 7.8V20h-2V7.8l-3.6 3.6L6 10z"/></svg>',
        down: '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M12 20l-6-6 1.4-1.4 3.6 3.6V4h2v12.2l3.6-3.6L18 14z"/></svg>',
        edit: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4L18.5 9.5a2.828 2.828 0 1 0-4-4L4 16v4"/><path d="M13.5 6.5l4 4"/></svg>',
        dup: '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M7 6V3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-3v3c0 .552-.45 1-1.007 1H4.007A1 1 0 0 1 3 21l.003-14c0-.552.45-1 1.007-1H7zM5.003 8L5 20h10V8H5.003zM9 6h8v10h2V4H9v2z"/></svg>',
        del: '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"/></svg>',
      };
      for (const block of this.blocks) {
        const key = block.key;
        const el = doc.querySelector(`[data-block_id="${key}"]`);
        if (!el || el.dataset.editorWired === "1") {
          continue;
        }
        el.dataset.editorWired = "1";
        const win = doc.defaultView;
        if (win && win.getComputedStyle(el).position === "static") {
          el.style.position = "relative";
        }
        el.addEventListener(
          "click",
          (e) => {
            if (
              e.target.closest(".editor-block-tools") ||
              e.target.closest(".editor-insert-zone")
            ) {
              return;
            }
            e.preventDefault();
            const found = this.blocks.find((b) => b.key === key);
            if (found) {
              this.showEditBlock(found, true);
            }
          },
          true
        );
        // Checked before the editor's own chrome is appended, otherwise the
        // "+ Insert" pill and the tool icons would count as content.
        if (this.isRenderedEmpty(el)) {
          el.classList.add("editor-empty");
          const empty = doc.createElement("div");
          empty.className = "editor-empty-state";
          const title = doc.createElement("span");
          title.className = "editor-empty-title";
          title.textContent = block.type?.name || "Block";
          const sub = doc.createElement("span");
          sub.className = "editor-empty-sub";
          sub.textContent = "Nothing added yet — click to add content";
          empty.appendChild(title);
          empty.appendChild(sub);
          el.appendChild(empty);
          // Product blocks fetch their contents after the first paint, so a
          // block can be empty now and filled a moment later. Without this the
          // marker would stick and wrongly report the block as empty.
          if (win && win.MutationObserver) {
            const observer = new win.MutationObserver(() => {
              if (this.isRenderedEmpty(el)) {
                return;
              }
              el.classList.remove("editor-empty");
              empty.remove();
              observer.disconnect();
            });
            observer.observe(el, {
              childList: true,
              subtree: true,
              characterData: true,
              attributes: true,
              attributeFilter: ["src", "srcset"],
            });
          }
        }
        const ring = doc.createElement("div");
        ring.className = "editor-ring";
        el.appendChild(ring);
        const label = doc.createElement("div");
        label.className = "editor-label";
        el.appendChild(label);
        const tools = doc.createElement("div");
        tools.className = "editor-block-tools";
        const mkBtn = (act, title, svg, danger = false) => {
          const btn = doc.createElement("button");
          btn.title = title;
          btn.innerHTML = svg;
          if (danger) {
            btn.className = "danger";
          }
          btn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.previewAction(act, key);
          });
          return btn;
        };
        tools.appendChild(mkBtn("up", "Move up", icons.up));
        tools.appendChild(mkBtn("down", "Move down", icons.down));
        tools.appendChild(mkBtn("edit", "Edit content", icons.edit));
        tools.appendChild(mkBtn("dup", "Duplicate", icons.dup));
        tools.appendChild(mkBtn("del", "Delete", icons.del, true));
        el.appendChild(tools);
        const zone = doc.createElement("div");
        zone.className = "editor-insert-zone";
        zone.innerHTML =
          '<span class="editor-insert-line"></span><span class="editor-insert-pill">+ Insert</span>';
        zone.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openAddBlock(this.pageJson.order.indexOf(key));
        });
        el.appendChild(zone);
      }
      this.applySelectionHighlight();
    },
    // "Empty" means the block rendered nothing a visitor could see: no text and
    // no media with a source. Judged from the rendered output rather than from
    // the block's settings, so it holds for any template's field names.
    // Walks the tree instead of using textContent/querySelectorAll so the
    // editor's own overlays are skipped — the "+ Insert" pill is text, and
    // counting it would make every block look non-empty on re-check.
    isRenderedEmpty(el) {
      const isChrome = (node) =>
        typeof node.className === "string" &&
        /(^|\s)editor-/.test(node.className);
      const walk = (node) => {
        for (const child of node.childNodes) {
          if (child.nodeType === 3) {
            if (child.textContent.replace(/\s+/g, " ").trim()) {
              return false;
            }
            continue;
          }
          if (child.nodeType !== 1 || isChrome(child)) {
            continue;
          }
          if (/^(img|video|iframe|source)$/i.test(child.tagName)) {
            const src = (
              child.getAttribute("src") ||
              child.getAttribute("srcset") ||
              ""
            ).trim();
            // A twig variable that resolved to nothing leaves the attribute
            // empty or holding the bare media prefix with no file after it.
            if (src && !/^(https?:)?\/\/[^/]+\/?$/.test(src)) {
              return false;
            }
          }
          if (!walk(child)) {
            return false;
          }
        }
        return true;
      };
      return walk(el);
    },
    async previewAction(act, key) {
      const block = this.blocks.find((b) => b.key === key);
      if (!block) {
        return;
      }
      this.showEditBlock(block);
      if (act === "up" || act === "down") {
        this.moveBlock(key, act === "up" ? -1 : 1);
      } else if (act === "edit") {
        if (block.type?.blocks?.some((b) => b.type !== "array")) {
          this.editContent(block.type.blocks, block.value.settings);
        }
      } else if (act === "dup") {
        await this.duplicateBlock();
      } else if (act === "del") {
        await this.deleteBlock();
      }
    },
    moveBlock(key, delta) {
      const order = this.pageJson.order;
      const i = order.indexOf(key);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= order.length) {
        return;
      }
      this.pushHistory();
      const otherKey = order[j];
      [order[i], order[j]] = [order[j], order[i]];
      const bi = this.blocks.findIndex((b) => b.key === key);
      const bj = this.blocks.findIndex((b) => b.key === otherKey);
      if (bi > -1 && bj > -1) {
        const arr = this.blocks;
        [arr[bi], arr[bj]] = [arr[bj], arr[bi]];
      }
      const doc = this.$refs.preview?.contentWindow?.document;
      const el = doc?.querySelector(`[data-block_id="${key}"]`);
      const other = doc?.querySelector(`[data-block_id="${otherKey}"]`);
      if (el && other && el.parentNode) {
        if (delta < 0) {
          el.parentNode.insertBefore(el, other);
        } else {
          el.parentNode.insertBefore(el, other.nextSibling);
        }
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      } else {
        this.loadJson();
      }
    },
    loadVersions() {
      if (!this.activePage?.id) {
        this.versions = [];
        return;
      }
      http
        .fetch(`/page/${this.activePage.id}/versions`)
        .then((response) => {
          this.versions = response?.data ?? [];
          this.viewedVersion = null;
        })
        .catch(() => {
          this.versions = [];
        });
    },
    async restoreVersion(version) {
      this.pushHistory();
      this.pageJson = JSON.parse(version.json);
      this.selectedBlock = undefined;
      this.dirty = true;
      this.viewedVersion = version.num;
      this.historyShow = false;
      await this.loadJson();
      this.toast("Version " + version.num + " restored");
    },
    async returnToNewest() {
      await this.loadPageRecord();
      this.viewedVersion = null;
      this.toast("Newest version restored");
    },
    formatWhen(iso) {
      try {
        return new Date(iso).toLocaleString();
      } catch (e) {
        return iso;
      }
    },
    findRealMove(oldOrder, newOrder) {
      if (!Array.isArray(oldOrder) || !Array.isArray(newOrder)) {
        return null;
      }

      let moved = null;
      let maxDelta = 0;

      for (const key of oldOrder) {
        const from = oldOrder.indexOf(key);
        const to = newOrder.indexOf(key);

        if (to === -1) continue;

        const delta = Math.abs(from - to);
        if (delta > maxDelta) {
          maxDelta = delta;
          moved = { key, from, to };
        }
      }

      return moved;
    },
    moveEvent() {
      const oldOrder = [...this.pageJson.order];

      this.pushHistory();
      const newOrder = this.blocks.map((block) => block.key);
      this.pageJson.order = newOrder;

      const realMove = this.findRealMove(oldOrder, newOrder);
      let doc = this.$refs.preview.contentWindow.document;
      const el = doc.querySelector(`[data-block_id="${realMove.key}"]`);
      if (el == undefined || el == null) {
        this.loadJson();
      } else {
        try {
          const parent = el.parentNode;
          parent.removeChild(el);
          const target = doc.querySelector(
            `[data-block_id="${newOrder[realMove.to + 1 ?? "----"]}"]`
          );
          if (target) {
            parent.insertBefore(el, target);
          } else {
            parent.appendChild(el);
          }
        } catch (e) {
          this.loadJson();
        }
      }
    },
    // Redraws one block in place. Returns whether it actually happened: the
    // caller falls back to rebuilding the whole page when it did not.
    reloadBlock(name, context, id) {
      const _this = this;
      let doc = this.$refs.preview.contentWindow.document;

      const el = doc.querySelector(`[data-block_id="${id}"]`);
      if (el == undefined || el == null) {
        return false;
      }

      // Twig's file loader only calls load() synchronously once the template
      // is already cached. This used to return true regardless, so on the
      // first edit of a block the caller believed the preview had been
      // redrawn while nothing had changed on screen — the edit only appeared
      // after some later action rebuilt the page.
      let rendered = false;
      Twig.twig({
        name: `layout/${name}.twig`,
        method: "file",
        async: false,
        load(template) {
          let html = template.render(context);

          const wrapper = document.createElement("div");
          wrapper.innerHTML = html.trim();

          const firstElement = wrapper.firstElementChild;
          if (firstElement) {
            firstElement.setAttribute("data-block_id", id);
            el.replaceWith(firstElement);
            _this.setupPreviewInteractions();
            rendered = true;
          }
        },
        error(err) {
          console.error("Failed to reload block:", err);
          _this.reportProblem(
            `Block could not be redrawn: ${name}`,
            err?.message ?? String(err ?? "template error")
          );
        },
      });

      return rendered;
    },
    // Head tags the storefront serves that the theme's JS depends on. Without
    // meta[name="webshop-id"] the cart script throws on null and takes the
    // whole theme bundle down with it, so the sticky header never receives its
    // .scroll class and stays transparent — content then shows through it.
    injectHeadTags(doc) {
      const shop =
        this.webshops.find((w) => w.id === this.previewShop) ??
        this.webshops.find((w) => w.is_default) ??
        this.webshops[0];
      if (!shop?.id || !doc?.head) {
        return;
      }
      // cookie-modal is read the same unguarded way by the storefront's
      // cookie script, which is loaded into the preview too.
      const tags = {
        "webshop-id": String(shop.id),
        "cookie-modal": "1",
      };
      for (const [name, content] of Object.entries(tags)) {
        let meta = doc.head.querySelector(`meta[name="${name}"]`);
        if (!meta) {
          meta = doc.createElement("meta");
          meta.setAttribute("name", name);
          doc.head.appendChild(meta);
        }
        meta.setAttribute("content", content);
      }
    },
    // Render one twig section to an HTML string (loadSection injects into the
    // preview instead). Gives up after ~4s so a missing file can't hang the
    // whole preview.
    renderSectionToString(name, context) {
      return new Promise((resolve) => {
        let attempts = 0;
        const interval = setInterval(() => {
          attempts += 1;
          if (attempts > 20) {
            clearInterval(interval);
            resolve("");
            return;
          }
          Twig.twig({
            name: `layout/${name}.twig`,
            method: "file",
            async: false,
            load(template) {
              clearInterval(interval);
              try {
                resolve(template.render(context));
              } catch (e) {
                console.warn("Global block render failed:", name, e);
                resolve("");
              }
            },
            error() {
              clearInterval(interval);
              resolve("");
            },
          });
        }, 200);
      });
    },
    // Render every section of a page (order + sections) into one HTML string
    async renderPageSections(json) {
      if (!json || !Array.isArray(json.order)) {
        return "";
      }
      let html = "";
      for (const key of json.order) {
        const section = json.sections?.[key];
        if (!section) {
          continue;
        }
        if (this.possibleBlocks[section.type]?.type === "featured-products") {
          try {
            section.products = await this.loadProducts(section);
          } catch (e) {
            section.products = { data: [] };
          }
        }
        html += await this.renderSectionToString("sections/" + section.type, {
          settings: section.settings || {},
          section,
        });
      }
      return html;
    },
    // The theme declares global dynamic blocks in config.json, e.g.
    // { content_for_infobox: { name: "Hamburger menu" } }. Each one is filled
    // by the dynamic page of that name — that's how the storefront renders the
    // header menu, so the preview has to do the same or the header comes out
    // shorter than the live site.
    async loadGlobalBlocks() {
      // Cache the in-flight promise, not just the result: loadJson() runs on
      // every render, and re-entering this (3 API calls + twig renders) would
      // keep the preview busy forever and re-trip the API rate limit.
      if (!globalBlockCache[this.template]) {
        globalBlockCache[this.template] = this.fetchGlobalBlocks();
      }
      this.globalBlocks = await globalBlockCache[this.template];
    },
    async fetchGlobalBlocks() {
      let defs = {};
      try {
        const config = await http.fetch(
          `/themes/template/${this.template}/file`,
          { file: "config.json" }
        );
        defs = config?.global_dynamic_blocks ?? {};
      } catch (e) {
        return {};
      }
      if (Object.keys(defs).length === 0) {
        return {};
      }

      let dynamicPages = [];
      try {
        const res = await http.fetch("/page/dynamic");
        dynamicPages = res.data ?? [];
      } catch (e) {
        return {};
      }

      const result = {};
      for (const [key, def] of Object.entries(defs)) {
        result[key] = "";
        const page = dynamicPages.find((p) => p.name === def?.name);
        if (!page) {
          console.warn(
            `Global block "${key}" expects a dynamic page named "${def?.name}" — not found.`
          );
          continue;
        }
        try {
          const full = await http.fetch("/page/" + page.id);
          result[key] = await this.renderPageSections(full.data?.json);
        } catch (e) {
          console.warn("Global block failed to render:", key, e);
        }
      }
      return result;
    },
    // The editor's own reports go into the same log as the uncaught errors,
    // so one list answers "what went wrong while I was working".
    reportProblem(title, detail) {
      recordProblem("editor", title, detail);
    },
    async loadSection(name, context, id = null) {
      const _this = this;
      return new Promise((resolve) => {
        // A template that never loads used to hang the whole editor: the
        // error branch neither stopped this timer nor settled the promise, so
        // it re-requested the file every second for ever, loadJsonInner's
        // Promise.all never returned, and jsonUnderLoad stayed true — after
        // which every later loadJson() returned at the guard and the preview
        // never updated again. One broken block killed the session. Now it
        // gives up after ~20s and the rest of the page still renders.
        // The retry is deliberate, not a workaround: twig's file loader
        // fetches includes asynchronously, so the first pass over a template
        // that includes anything always fails and the next one succeeds. What
        // was missing is a limit — and a way out when it never succeeds.
        let attempts = 0;
        let lastError = "";
        const giveUp = (why) => {
          clearInterval(interval);
          console.warn(`Block "${id ?? name}" left out of the preview:`, why);
          _this.reportProblem(`Block could not be rendered: ${name}`, why);
          // Empty string, not nothing: the caller joins the results by
          // position, so a failed block must still hold its place.
          resolve("");
        };
        const interval = setInterval(() => {
          attempts += 1;
          if (attempts > 20) {
            giveUp(lastError || "its template did not load in 20 seconds");
            return;
          }
          Twig.twig({
            name: `layout/${name}.twig`,
            method: "file",
            async: false,
            load(template) {
              let html = template.render(context);
              const wrapper = document.createElement("div");
              wrapper.innerHTML = html.trim();

              const elements = Array.from(wrapper.childNodes).filter(
                (node) =>
                  node.nodeType === Node.ELEMENT_NODE ||
                  (node.nodeType === Node.TEXT_NODE &&
                    node.textContent.trim() !== "")
              );

              const firstElement = wrapper.firstElementChild;
              if (elements.length === 1 && firstElement) {
                firstElement.setAttribute("data-block_id", id);
              }

              html = wrapper.innerHTML;
              clearInterval(interval);
              if (_this.loaded.var < 90) {
                _this.loaded.var += 5;
                _this.loaded.bar.set(_this.loaded.var);
              }
              // The HTML goes back to the caller instead of being appended
              // here: the blocks render in parallel, so appending put them in
              // the order they happened to finish — a template already in the
              // cache beat one that still had to be fetched, and the preview
              // ended up in a different order than the block list.
              resolve(html);
            },
            error(err) {
              // Keep the reason for the give-up message; the next tick may
              // still succeed once the includes have arrived.
              lastError = err?.message ?? String(err ?? "template error");
            },
          });
        }, 1000);
      });
    },
    async loadJson() {
      if (
        this.jsonUnderLoad ||
        this.template === undefined ||
        this.possibleBlocks.length < 1
      ) {
        return;
      }
      try {
        await this.loadJsonInner();
      } catch (e) {
        console.error("[loadJson ERROR]", e);
        this.jsonUnderLoad = false;
      }
    },
    async loadJsonInner() {
      this.jsonUnderLoad = true;
      this.blocks = [];
      this.contentForLayout = "";
      if (this.pageJson === null) {
        this.pageJson = {
          sections: {},
          order: [],
        };
      }
      let loads = [];

      for (const key of this.pageJson.order) {
        const section = this.pageJson.sections[key];
        const context = {
          settings: section.settings || {},
          section,
        };

        if (this.possibleBlocks[section.type]?.type === "featured-products") {
          try {
            section.products = await this.loadProducts(section);
          } catch (e) {
            // a failing product search must not break the whole preview
            console.warn("Product search failed for block", key, e);
            section.products = { data: [] };
          }
        }

        if (this.blocks.findIndex((e) => e.key === key) < 0) {
          this.blocks.push({
            key: key,
            typeName: section.type,
            type: this.possibleBlocks[section.type],
            value: section || {},
          });
        }
        loads.push(this.loadSection("sections/" + section.type, context, key));
      }
      // Joined by position, so the preview follows the page order rather than
      // whichever template finished first.
      const rendered = await Promise.all(loads);
      this.contentForLayout = rendered.join("");
      // must be ready before theme.twig renders — it fills header variables
      await this.loadGlobalBlocks();
      this.loaded.content = true;
      this.loadTwig();
      this.jsonUnderLoad = false;
    },
    async initTwig() {
      const _this = this;

      Twig.extend(function (Twig) {
        Twig.Templates.registerLoader(
          "file",
          function (location, params, callback, errorCallback) {
            _this.loaded.var += 5;
            _this.loaded.bar.set(_this.loaded.var);
            if (location.startsWith("layout/sections")) {
              location = location.replace("layout/", "");
            } else if (location.startsWith("sections/snippets")) {
              location = location.replace("sections/snippets", "snippets");
            }

            if (_this.files[location] !== undefined) {
              params.data = _this.files[location];
              const template = new Twig.Template(params);
              if (callback !== undefined) {
                callback(template);
              }
              return template;
            }

            http
              .fetch(
                "/themes/template/" + _this.template + "/file",
                {
                  file: location,
                },
                false,
                null,
                false
              )
              .then((data) => {
                _this.files[location] = data;
                params.data = data;
                const template = new Twig.Template(params);
                if (callback !== undefined) {
                  callback(template);
                }
                return template;
              })
              .catch(errorCallback);
          }
        );
      });

      Twig.extendFunction("include_html", function (value) {
        return value;
      });

      Twig.extendFunction("linklists", function (menuId) {
        return _this.resolveMenuItems(menuId);
      });

      Twig.extendFilter("asset_url", function (value) {
        return `/templates/${_this.template}/assets/${value}`;
      });

      Twig.extendFilter("t", function (value) {
        if (typeof value === "object" && Object.keys(value)?.length > 0) {
          return value[_this.language] ?? value[Object.keys(value)[0]];
        }
        return value;
      });
      Twig.extendFilter("trans", function (value) {
        return _this.translations[value] ?? value;
      });
      // The engine's `default` takes a second argument (the locale of the
      // text: `'key'|trans|default('Text', 'hu')`), which Twig.js refuses.
      // A translation key the editor has no text for comes back from `trans`
      // as the key itself, so a dotted key that is not in the translations
      // counts as empty, like the engine's empty string.
      Twig.extendFilter("default", function (value, params) {
        const isMissingKey =
          typeof value === "string" &&
          /^[\w-]+(\.[\w-]+)+$/.test(value) &&
          _this.translations[value] === undefined;
        const empty =
          value === undefined ||
          value === null ||
          value === false ||
          value === "" ||
          (Array.isArray(value) && value.length === 0) ||
          isMissingKey;
        if (!empty) {
          return value;
        }
        const fallback = params?.[0];
        if (fallback === undefined || fallback === null) {
          return "";
        }
        if (typeof fallback === "object" && !Array.isArray(fallback)) {
          return (
            fallback[params?.[1]] ??
            fallback[_this.language] ??
            fallback[Object.keys(fallback)[0]] ??
            ""
          );
        }
        return fallback;
      });
      // Links: the preview has no shop prefix (language / sale channel slug).
      Twig.extendFilter("shop_link", function (value) {
        return value;
      });
      Twig.extendFilter("localized_url", function (value) {
        return value;
      });
      Twig.extendFunction("shop_url", function (path) {
        return path;
      });
      // The engine inlines the Google Fonts stylesheet and serves the font files
      // from the shop's own domain (/static/fonts). The editor has no such
      // proxy, so the preview links the stylesheet straight from Google. Only
      // Google Fonts stylesheets are accepted, like in the engine.
      Twig.extendFunction("remote_font", function (font) {
        let url = String(font ?? "").trim();
        if (!/^(https?:\/\/|\/)/.test(url)) {
          if (!/^[A-Za-z0-9 +]+(:[A-Za-z0-9@;,.-]+)?$/.test(url)) {
            return "";
          }
          url = `https://fonts.googleapis.com/css2?family=${url.replace(
            / /g,
            "+"
          )}&display=swap`;
        }
        url = url.replace(
          /^\/static\/fonts\//,
          "https://fonts.googleapis.com/"
        );
        if (
          !/^https:\/\/fonts\.googleapis\.com\/css2?\?[A-Za-z0-9%+:;,.@=&_-]+$/.test(
            url
          )
        ) {
          return "";
        }
        return `<link rel="stylesheet" href="${url}">`;
      });

      Twig.extendFilter("image_url", function (value, args) {
        const params = args?.[0] || {};

        if (typeof value === "string") {
          const variant = imageVariant(
            value,
            SIZE_TO_WIDTH[params.size] ?? 720
          );
          if (variant) {
            return variant;
          }
          const query = new URLSearchParams(params).toString();
          const url = MEDIA_BASE + value;
          return query ? `${url}?${query}` : url;
        }
        if (typeof value === "object" && value?.src) {
          const query = new URLSearchParams(params).toString();
          return query ? `${value.src}?${query}` : value.src;
        }
        return "";
      });

      Twig.extendFilter("image_srcset", function (value) {
        if (typeof value === "string") {
          const set = imageSrcset(value);
          return set || MEDIA_BASE + value;
        }

        if (typeof value === "object") {
          if (value?.sizes) {
            return Object.entries(value.sizes)
              .map(([width, url]) => `${url} ${width}w`)
              .join(", ");
          }

          if (value?.src) {
            return value.src;
          }
        }

        return "";
      });

      Twig.extendFilter("video_srcset", function (value) {
        if (typeof value === "object") {
          return JSON.stringify([
            {
              src: value.path + "/360p.webm",
              type: "video/webm",
              quality: 360,
            },
            { src: value.path + "/360p.mp4", type: "video/mp4", quality: 360 },
            {
              src: value.path + "/480p.webm",
              type: "video/webm",
              quality: 480,
            },
            { src: value.path + "/480p.mp4", type: "video/mp4", quality: 480 },
            {
              src: value.path + "/720p.webm",
              type: "video/webm",
              quality: 720,
            },
            { src: value.path + "/720p.mp4", type: "video/mp4", quality: 720 },
          ]);
        }
        return "";
      });
      Twig.extendFilter("video_poster", function (value) {
        if (typeof value === "object") {
          return value.path + "/frame1.jpg";
        }
        return "";
      });

      Twig.extendFilter("money", function (value) {
        if (typeof value === "number") {
          return value.toLocaleString("hu-HU", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
        } else if (typeof value === "object") {
          return value.amount.toLocaleString("hu-HU", {
            style: "currency",
            currency: value.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: value.currency === "HUF" ? 0 : 2,
          });
        }

        return "123 €";
      });
    },
    loadTwig() {
      if (this.interval !== null && this.interval !== undefined) {
        return;
      }
      const _this = this;

      let doc = this.$refs.preview.contentWindow.document;

      const defaultShop =
        this.webshops.find((w) => w.id === this.previewShop) ??
        this.webshops.find((w) => w.is_default) ??
        this.webshops[0] ??
        {};

      const context = {
        now: Math.floor(Date.now() / 1000),
        page_title: this.activePage?.name ?? "My Page",
        template: "index", // vagy pl. "product", "collection", "cart", stb.

        shop: {
          name: defaultShop.name ?? "My Webshop",
          email: defaultShop.email ?? "info@mywebshop.com",
          domain: defaultShop.domain ?? "mywebshop.com",
          url: `https://${defaultShop.domain ?? "mywebshop.com"}`,
          currency: defaultShop.currency ?? "EUR",
          money_format: "{{ amount_no_decimals_with_space_separator }} Ft",
          description: defaultShop.description ?? "",
        },

        settings: {
          logo: "https://vivienvance.com/images/logo.png",
          favicon: "/assets/favicon.png",
          type_header_font: { system: false },
          type_body_font: { system: false },
          color_primary: "#f03e3e",
          show_cart_icon: true,
          show_search: true,
          store_locale: "hu",
        },

        cart: {
          item_count: 2,
          total_price: 32970,
          items: [
            {
              title: "MARE BIKINI TOP",
              quantity: 1,
              price: 10990,
              line_price: 10990,
              image: "https://cdn.shopify.com/s/files/1/xxx/products/1.jpg",
              url: "/products/mare-bikini-top",
              variant_title: "L",
            },
            {
              title: "ZAFFIRO PAMUT TANGA ALSÓ",
              quantity: 2,
              price: 10990,
              line_price: 21980,
              image: "https://cdn.shopify.com/s/files/1/xxx/products/2.jpg",
              url: "/products/zaffiro-tanga",
              variant_title: "L",
            },
          ],
        },

        collection: {
          title: "Bikini Kollekció",
          description: "Nyári bikini választék",
          handle: "bikini-kollekcio",
          products: [
            {
              title: "MARE BIKINI TOP",
              price: 10990,
              featured_image: "/assets/bikini.jpg",
              vendor: "Vivien Vance",
              url: "/products/mare-bikini-top",
            },
            {
              title: "ZAFFIRO PAMUT TANGA ALSÓ",
              price: 10990,
              featured_image: "/assets/tanga.jpg",
              vendor: "Vivien Vance",
              url: "/products/zaffiro-tanga",
            },
          ],
        },

        product: {
          title: "MARE BIKINI TOP",
          price: 10990,
          compare_at_price: 13990,
          description: "<p>Részletes leírás itt...</p>",
          handle: "mare-bikini-top",
          images: ["/assets/bikini1.jpg", "/assets/bikini2.jpg"],
          options: ["Méret", "Szín"],
          variants: [
            {
              id: 1,
              title: "L / Piros",
              available: true,
              price: 10990,
              image: "/assets/bikini1.jpg",
            },
          ],
          url: "/products/mare-bikini-top",
        },

        customer: {
          first_name: "Anna",
          last_name: "Kiss",
          email: "anna@example.com",
          logged_in: true,
        },

        current_tags: ["summer", "sale"],
        current_page: 2,
        request: { locale: { iso_code: "hu" } },
        routes: {
          cart_url: "/cart",
          account_url: "/account",
          search_url: "/search",
        },

        // Note: the theme renders content_for_header inside <body>, so head
        // tags can't go here — they're appended to <head> after the write
        // below, the way the storefront serves them.
        content_for_header: "",
        content_for_layout: _this.contentForLayout,
        // theme-declared global blocks (header menu etc.)
        ..._this.globalBlocks,

        section: {
          id: "featured-collection",
          settings: {
            title: "Kiemelt termékek",
            featured_collection: {
              title: "Nyári Bikini Kollekció",
              products: [], // opcionálisan töltsd
            },
            number_of_products_to_show: 4,
          },
        },

        // Például navigation menühöz
        linklists: {
          main_menu: [
            { title: "Home", url: "/", active: false },
            { title: "Bikini", url: "/bikini", active: true },
            { title: "Fehérnemű", url: "/lingerie", active: false },
            { title: "Fitness", url: "/fitness", active: false },
          ],
        },
      };

      // Same trap as loadSection had: with only a console.error in the error
      // branch this timer re-requested theme.twig five times a second for as
      // long as the tab stayed open, which is what made the editor crawl and
      // then stop responding. It gives up after ~10s and says so.
      // theme.twig includes header.twig and footer.twig, which twig's loader
      // fetches asynchronously — so the first render attempts fail by design
      // and a later one succeeds. Only the missing limit was the bug.
      let themeAttempts = 0;
      let themeError = "";
      this.interval = setInterval(() => {
        themeAttempts += 1;
        if (themeAttempts > 150) {
          clearInterval(_this.interval);
          _this.interval = null;
          _this.reportProblem(
            "The theme did not load",
            themeError ||
              "layout/theme.twig did not render in 30 seconds — reload the page"
          );
          return;
        }
        // Twig renders synchronously here, and while header/footer are still
        // being fetched it throws a TypeError ("reading 'valueOf'") out of its
        // own promise chain. That is the expected "not ready yet" state, so
        // it is caught and the next tick retries instead of flooding the
        // console with uncaught errors.
        try {
          Twig.twig({
            name: "layout/theme.twig",
            method: "file",
            async: false,
            load(template) {
              // A render already in flight would otherwise freeze the context
              // captured when loadTwig() was called — re-read the parts that
              // finish asynchronously so the header menu is never left out.
              Object.assign(context, _this.globalBlocks, {
                content_for_layout: _this.contentForLayout,
              });
              doc.open();
              doc.write(template.render(context));
              doc.close();
              // Must run before setScript(): the theme's cart JS reads
              // document.head's meta[name="webshop-id"] and throws on null.
              _this.injectHeadTags(doc);
              clearInterval(_this.interval);
              _this.interval = null;
              if (_this.loaded.var < 50) {
                _this.loaded.var = 50;
                _this.loaded.bar.set(_this.loaded.var);
              }
              _this.loaded.page = true;
              _this.setCss();
              _this.setScript();
              _this.applyHidden();
              _this.setupPreviewInteractions();
            },
            error(err) {
              // Recorded, not fatal: the includes may still be on their way.
              themeError = err?.message ?? String(err ?? "template error");
            },
          });
        } catch (err) {
          themeError = err?.message ?? String(err ?? "template error");
        }
      }, 200);

      this.calcRate();
    },
    async loadBlocks() {
      const blocks = await http.fetch(
        "/themes/template/" + this.template + "/file",
        {
          file: "blocks.json",
        }
      );
      this.possibleBlocks = blocks;
      registerTemplateIcons(blocks);
    },
    // Load the complete translation map for the active language in one request
    // so the twig `trans` filter matches the live storefront without request
    // fan-out across every category.
    async loadTranslations() {
      const lang = this.language;
      if (!translationCache[lang]) {
        // cache the in-flight promise immediately: two concurrent callers
        // (initial load + language init) must not download every file twice
        translationCache[lang] = (async () => {
          const response = await http.fetch(`/translation/preview/${lang}`);
          return response?.data?.translations ?? {};
        })().catch(() => {
          delete translationCache[lang];
          return {};
        });
      }
      this.translations = await translationCache[lang];
    },
    // Names for the ids a visibility rule compares against. Only dynamic
    // pages have rules at all, so a static page asks for nothing.
    async loadConditionCategories() {
      if (!this.activePage?.page_type) {
        return;
      }
      const lang = this.language ?? "en";
      if (!conditionCategoryCache[lang]) {
        conditionCategoryCache[lang] = http
          .fetch("/products/categories/products/" + lang)
          .then((data) => flattenCategories(data?.data ?? []))
          .catch(() => {
            delete conditionCategoryCache[lang];
            return [];
          });
      }
      this.conditionCategories = await conditionCategoryCache[lang];
    },
    fieldLabel,
    // A menu edited from inside the block dialog: the menu record itself is
    // already updated in place, so only the preview is behind. Re-rendering
    // this one block is enough — the menu is what it draws.
    async menuChanged({ id, items }) {
      const menu = (this.menusData ?? []).find(
        (one) => String(one.id) === String(id)
      );
      if (menu) {
        menu.menu_items = items;
      }
      const upgraded = this.reloadBlock(
        "sections/" + this.selectedBlock?.typeName,
        {
          settings: this.selectedBlock?.value?.settings || {},
          section: this.selectedBlock?.value,
        },
        this.selectedBlock?.key
      );
      if (!upgraded) {
        await this.loadJson();
      }
    },
    async loadMenus() {
      try {
        const res = await http.fetch("/menu");
        this.menusData = res.data ?? [];
      } catch (e) {
        this.menusData = [];
      }
    },
    resolveMenuItems(menuId) {
      const menu = (this.menusData ?? []).find(
        (m) => String(m.id) === String(menuId) || m.slug === menuId
      );
      if (!menu) {
        return [];
      }
      return (menu.menu_items ?? []).map((item) => {
        let url = "#";
        if (item.target_url) {
          url = item.target_url;
        } else if (
          item.target_category_id !== null &&
          item.target_category_id !== undefined
        ) {
          url = `/category/${item.target_category_id}-${item.slug ?? ""}`;
        } else if (
          item.target_page_id !== null &&
          item.target_page_id !== undefined
        ) {
          url = `/${item.target_page_id}-${item.slug ?? ""}`;
        }
        const translations = item.translations ?? {};
        return {
          title:
            translations[this.language] ??
            translations[Object.keys(translations)[0]] ??
            "",
          url,
          active: false,
        };
      });
    },
    save() {
      if (this.saving) return;
      this.saving = true;
      this.loaded.var = 10;
      this.loaded.bar.set(this.loaded.var);
      this.loaded.script = false;

      this.loaded.var = 40;
      this.loaded.bar.set(this.loaded.var);

      let pageData = this.pageJson;

      for (const key in pageData.sections) {
        if (typeof pageData.sections[key].products === "object") {
          delete pageData.sections[key].products;
        }
      }

      http
        .fetch(
          `/page/${this.activePage.id}`,
          {
            json: JSON.stringify(pageData),
            editor_version: this.activePage.editor_version,
          },
          true,
          "PUT"
        )
        .then((response) => {
          this.loaded.var = 90;
          this.loaded.bar.set(this.loaded.var);
          this.loaded.script = true;
          this.dirty = false;
          this.activePage.editor_version =
            response?.data?.editor_version ??
            (this.activePage.editor_version || 0) + 1;
          this.loadVersions();
          this.saveStatus = "Saved just now";
          this.toast("Page saved");
          this.saving = false;
        })
        .catch(() => {
          this.loaded.script = true;
          this.saveStatus = "";
          this.toast("Save failed — your changes are NOT saved yet");
          this.saving = false;
        });
    },
    getDimensions() {
      this.calcRate();
    },
    generateID(length = 8) {
      return Math.random().toString(36).substr(2, length);
    },
    // Settings a freshly added block starts with. Read from the template's
    // own style schema (never a hardcoded block list): full-width blocks look
    // right by default, so any "no horizontal whitespace" toggle starts on.
    defaultBlockSettings(type) {
      const settings = {};
      const block = this.possibleBlocks[type];
      // A product list leaves sold-out items out unless the block asks for
      // them, so every freshly added product block had to have "Show sold
      // out" found and switched on by hand. A new one starts with it on.
      // Blocks already on a page keep whatever they hold: an empty value
      // there is somebody's earlier choice, not a missing default.
      let listsProducts = block?.type === "featured-products";
      for (const sub of block?.blocks ?? []) {
        if (
          sub?.type === "featured-products" ||
          sub?.type === "featured-posts"
        ) {
          listsProducts = true;
        }
        for (const style of sub?.styles ?? []) {
          if (
            style?.key &&
            style?.class &&
            /no\s*horizontal\s*whitespace/i.test(style.name ?? "")
          ) {
            settings[style.key] = style.class;
          }
        }
      }
      if (listsProducts) {
        settings.showSoldOut = true;
      }
      return settings;
    },
    async addBlock(type) {
      this.pushHistory();
      let key = type;
      if (this.pageJson.sections[type] !== undefined) {
        key += this.generateID();
      }
      this.pageJson.sections[key] = {
        type: type,
        settings: this.defaultBlockSettings(type),
      };

      if (
        this.possibleBlocks[type]?.dynamic_data !== undefined &&
        this.possibleBlocks[type]?.dynamic_data !== null
      ) {
        this.pageJson.sections[key].dynamic_data =
          this.possibleBlocks[type]?.dynamic_data;
      }

      if (
        this.insertIndex !== null &&
        this.insertIndex >= 0 &&
        this.insertIndex < this.pageJson.order.length
      ) {
        // insert at a specific position (preview "+ Insert" zones)
        this.pageJson.order.splice(this.insertIndex, 0, key);
        this.insertIndex = null;
        this.selectedBlock = undefined;
        await this.loadJson();
        return;
      }
      this.insertIndex = null;

      this.pageJson.order.push(key);

      let section = this.pageJson.sections[key];

      if (this.blocks.findIndex((e) => e.key === key) < 0) {
        this.blocks.push({
          key: key,
          typeName: type,
          type: this.possibleBlocks[section.type],
          value: section || {},
        });
        const context = {
          settings: section.settings || {},
          section,
        };
        this.contentForLayout += await this.loadSection(
          "sections/" + section.type,
          context,
          key
        );
      }
      this.loadTwig();
    },
    calcRate() {
      if (
        !this.$refs.outsideContainer ||
        !this.$refs.container ||
        !this.$refs.preview
      ) {
        return;
      }
      // No CSS zoom on the iframe: zoom changes the layout viewport and
      // renders every px larger/smaller than the live site. The container
      // transform below only scales visually, keeping sizes faithful.
      this.zoom = undefined;

      const preview = this.$refs.preview;
      if (preview.contentWindow) {
        if (this.size === "wide") {
          const doc = preview.contentWindow.document;
          const style = doc.querySelector(".css-phone");
          if (style) style.remove();
        } else {
          let doc = preview.contentWindow.document;

          if (!doc.querySelector(".css-phone")) {
            let style = document.createElement("STYLE");
            style.classList.add("css-phone");
            style.innerHTML = "body::-webkit-scrollbar {display: none;}";
            doc.head.appendChild(style);
          }
        }
      }

      const maxW = this.$refs.outsideContainer.offsetWidth - 20;
      const actualW = this.$refs.container.offsetWidth;
      let rate = maxW < actualW ? maxW / actualW : 1;
      if (this.size !== "wide") {
        // device frames must also fit vertically (the iPhone frame is a
        // real-proportion 868px tall) — scale visually, never distort
        const maxH = this.$refs.outsideContainer.offsetHeight - 16;
        const actualH = this.$refs.container.offsetHeight;
        if (actualH > 0 && maxH / actualH < rate) {
          rate = maxH / actualH;
        }
      }
      // scale() shrinks what is painted but not what is laid out, so at
      // rate 0.92 a full-height container still painted 8% short of the
      // column beside it. Laying it out taller by exactly 1/rate makes the
      // painted height land on the column height. The figure was already
      // being computed here and then never applied to the element.
      if (rate < 1 && this.size === "wide") {
        this.height = this.$refs.outsideContainer.offsetHeight * (1 / rate);
      } else {
        this.height = undefined;
      }
      this.rate = rate;
    },
    async deleteBlock() {
      const name =
        this.selectedBlock?.value?.block_name ??
        this.selectedBlock?.type?.name ??
        "this block";
      if (!window.confirm(`Delete “${name}”? You can undo with Ctrl+Z.`)) {
        return;
      }
      this.pushHistory();
      let i = this.pageJson.order.findIndex(
        (e) => e === this.selectedBlock.key
      );
      delete this.pageJson.sections[this.selectedBlock.key];
      this.pageJson.order.splice(i, 1);
      this.blocks.splice(i, 1);

      // data-block_id only tags the block's FIRST rendered element, so pulling
      // that one node out left every later sibling behind as an empty strip.
      // Re-rendering is slower but is the only thing that is right for a
      // section that emits more than one top-level element.
      this.selectedBlock = null;
      await this.loadBlocks();
      await this.loadJson();
      this.toast("Block deleted");
    },
    async deleteSubBlock(key, i) {
      this.pushHistory();
      this.selectedBlock.value.settings[key].splice(i, 1);

      const section = this.selectedBlock.value;

      if (
        this.selectedBlock.type?.type === "featured-products" &&
        (section.settings.featured_products === undefined ||
          section.settings.featured_bundles === undefined)
      ) {
        try {
          section.products = await this.loadProducts(section);
        } catch (e) {
          console.warn("Product search failed", e);
        }
      }

      const context = {
        settings: section.settings || {},
        section,
      };

      let upgraded = this.reloadBlock(
        "sections/" + this.selectedBlock.typeName,
        context,
        this.selectedBlock.key
      );
      if (!upgraded) {
        await this.loadBlocks();
        await this.loadJson();
      }
    },
    getSectionTitle(content, block) {
      const main = block.blocks.find((e) => e.main);
      if (!main) return "";

      const value = content?.[main.key];
      if (!value || typeof value !== "object") return "";

      const lang = this.language;
      const fallback = this.activePage?.default_language;

      const firstKey = Object.keys(value)[0];

      if (
        main.translate && Array.isArray(value)
          ? value.length > 0
          : Object.keys(value).length > 0
      ) {
        return (
          (lang && value[lang]) ??
          (fallback && value[fallback]) ??
          (firstKey ? value[firstKey] : "") ??
          ""
        );
      }

      return value ?? "";
    },
    editContent(block, settings) {
      this.editContentBlock = block;
      this.editContentSettings = settings;
      this.editContentShow = true;
    },
    addNewBlockItem(block) {
      this.pushHistory();
      if (this.selectedBlock.value.settings[block.key] === undefined) {
        this.selectedBlock.value.settings[block.key] = [];
      }
      this.selectedBlock.value.settings[block.key].push({});
    },
    // The theme's JS bundle for the preview. The storefront build
    // (core-webshop-engine, ThemeSyncService::bundleJs) inlines every
    // `require('./x.js')` of the entry files listed in config.json "js" once,
    // in order; this does the same from the template's repository, so the
    // preview runs the theme as it is in git. Kept for the browser session.
    async buildThemeScript() {
      const cacheKey = "theme-js-" + this.template;
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached && cached.length > 100) {
          return cached;
        }
      } catch (e) {
        // no session storage: build every time
      }

      const fetchFile = (file) =>
        http.fetch(
          "/themes/template/" + this.template + "/file",
          { file },
          false,
          null,
          false
        );

      let config = await fetchFile("config.json");
      if (typeof config === "string") {
        config = JSON.parse(config);
      }
      const entries = (config.js ?? [])
        .filter((item) => item.output === "app.js")
        .flatMap((item) => item.input ?? []);
      if (entries.length === 0) {
        entries.push("app.js");
      }

      // dir + relative path, with ./ and ../ resolved
      const resolve = (dir, relative) => {
        const parts = (dir ? dir.split("/") : []).concat(relative.split("/"));
        const out = [];
        for (const part of parts) {
          if (part === "..") {
            out.pop();
          } else if (part !== "." && part !== "") {
            out.push(part);
          }
        }
        return out.join("/");
      };

      const included = {};
      const bundle = async (path) => {
        if (included[path]) {
          return "";
        }
        included[path] = true;
        let source;
        try {
          source = await fetchFile("js/" + path);
        } catch (e) {
          console.warn("Theme JS not found:", path);
          return "";
        }
        if (typeof source !== "string") {
          source = JSON.stringify(source);
        }
        const dir = path.includes("/")
          ? path.slice(0, path.lastIndexOf("/"))
          : "";
        // replace sequentially: every file is inlined once, at its first require
        const pattern = /require\(['"](.+?)['"]\);?/g;
        let result = "";
        let last = 0;
        let match;
        while ((match = pattern.exec(source)) !== null) {
          let target = resolve(dir, match[1]);
          if (!target.endsWith(".js")) {
            target += ".js";
          }
          result += source.slice(last, match.index);
          result += (await bundle(target)) + "\r\n";
          last = match.index + match[0].length;
        }
        result += source.slice(last);
        return result + "\n";
      };

      let code = "";
      for (const entry of entries) {
        code += (await bundle(entry)) + "\n";
      }

      try {
        sessionStorage.setItem(cacheKey, code);
      } catch (e) {
        // too big or blocked: fine, it is only a cache
      }
      return code;
    },
    setScript() {
      if (!this.loaded.page || !this.loaded.style) {
        return;
      }
      const _this = this;
      // `scriptBuilding`: the build is asynchronous, a second call while it
      // runs would execute the theme JS twice (top-level `const`s collide)
      if (!_this.loaded.script && !_this.scriptBuilding) {
        _this.scriptBuilding = true;
        let doc = _this.$refs.preview.contentWindow.document;

        // Scripts that arrived through document.write() never executed, so the
        // theme's own bundle (sticky-header behaviour, sliders, toggles) was
        // dead in the preview. Re-append them now that the document is
        // complete — reading the src off the page keeps this theme-agnostic.
        // A unique query is required: with the identical URL the browser
        // treats the script as already loaded and skips execution.
        const runToken = Date.now();
        doc.querySelectorAll("script[src]").forEach((old) => {
          if (!old.src || old.dataset.reexecuted === "1") {
            return;
          }
          old.dataset.reexecuted = "1";
          const copy = doc.createElement("script");
          copy.src =
            old.src + (old.src.includes("?") ? "&" : "?") + "run=" + runToken;
          copy.dataset.reexecuted = "1";
          doc.head.appendChild(copy);
        });

        const script2 = document.createElement("script");
        script2.src = "/readyState.js";
        script2.onload = async function () {
          // The theme's own JS, built from the template like the storefront
          // build does (ThemeSyncService::bundleJs).
          try {
            const script = doc.createElement("script");
            script.textContent = await _this.buildThemeScript();
            doc.head.appendChild(script);
          } catch (error) {
            console.error("Theme JS failed to build:", error);
          }
          _this.scriptBuilding = false;
          _this.loaded.script = true;
        };
        doc.head.appendChild(script2);
      }
    },
    setCss() {
      if (!this.loaded.page || !this.loaded.content) {
        return;
      }
      this.loaded.var = 98;
      this.loaded.bar.set(this.loaded.var);

      let doc = this.$refs.preview.contentWindow.document;

      // The theme's CSS, compiled here with the current global variables.
      if (
        this.generatedCss?.length > 0 &&
        !doc.querySelector(".generated-css-main")
      ) {
        let div = document.createElement("STYLE");
        div.classList.add("generated-css-main");
        div.innerHTML = this.generatedCss;
        doc.head.appendChild(div);
      }

      this.loaded.style = true;
      this.loaded.var = 99;
      this.loaded.bar.set(this.loaded.var);

      this.setScript();
    },
    changeGlobalSettingsModal() {
      this.getGlobalSettings().then((variables) => {
        this.globalVariables = variables;
        this.globalShettingsShow = true;
      });
    },
    addImportCss(src) {
      let doc = this.$refs.preview.contentWindow.document;
      const style = document.createElement("style");
      style.innerText = `@import url('${src}');`;
      doc.head.appendChild(style);
    },
    getGlobalSettings() {
      return new Promise((resolve) => {
        http
          .fetch(
            "/themes/template/" + this.template + "/file",
            {
              file: `css/variables.scss`,
            },
            false,
            null,
            false
          )
          .then((scss) => {
            let colors = scss.matchAll(
              /\$([\S]{0,100})[\s]{0,4}:[\s]{0,4}(#[0-9ABCDEF]{3,6}|rgb[a]{0,1}\([0-9,.;% ]{3,30}\))/gim
            );
            let everyVariables = scss.matchAll(
              /\$([\S]{0,100})[\s]{0,4}:[\s]{0,4}([^\n;]{0,100})[\s;]{0,4}(\/\*[\s]{0,4}([^/]{0,100})[\s]{0,4}\*\/){0,1}/gim
            );

            let variables = {};
            let types = ["font-family", "font-size", "line-height"];

            for (const match of colors) {
              variables[match[1]] = { value: match[2], type: "color" };
            }
            for (const match of everyVariables) {
              if (variables[match[1]] === undefined) {
                let type = undefined;
                if (match[4] !== undefined && types.indexOf(match[4]) > -1) {
                  type = match[4];
                  match[2] = match[2].replace("px", "");
                }
                variables[match[1]] = { value: match[2], type: type };
              }
            }
            resolve(variables);
          });
      });
    },
    changeStyle(key, val) {
      let doc =
        this.$refs.preview.contentWindow.document.querySelector(":root");
      doc.style.setProperty("--" + key, val);
    },
    fixModernRgb(css) {
      // libsass can't parse the modern space-separated color syntax
      // (e.g. rgb(0 0 0 / .21)) — rewrite it to the legacy rgba() form.
      if (typeof css !== "string") {
        return css;
      }
      return css.replace(
        /rgba?\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*\/\s*([\d.]+%?)\s*\)/g,
        "rgba($1, $2, $3, $4)"
      );
    },
    generateSass() {
      this.generatedCss = "";

      // Dart Sass, the compiler the storefront's own build uses: the old
      // libsass build (sass.js) cannot compile min(100vw, calc(...)) or mixed
      // units. The package's browser entry (sass.default.cjs) is required by
      // path because webpack 4 does not read the package `exports` map.
      const sass = require("sass/sass.default.cjs");
      const _this = this;

      // Older cache keys held a libsass compile (stale, or "undefined" from a
      // failed one) - never reused.
      localStorage.removeItem("sass");
      localStorage.removeItem("sass-" + this.template);

      const cacheKey = "sass-dart-" + this.template;
      const value = localStorage.getItem(cacheKey);
      // Only trust the cache if it plausibly is real CSS.
      if (value !== null && value.length > 100 && value !== "undefined") {
        _this.generatedCss = value;
        _this.setCss();
        return;
      }
      localStorage.removeItem(cacheKey);

      const fetchFile = (file) =>
        http.fetch(
          "/themes/template/" + _this.template + "/file",
          { file },
          false,
          null,
          false
        );
      // `@import "x.css"` stays a plain CSS import in Dart Sass (a url the
      // preview cannot resolve): without the extension it goes through the
      // importer, which inlines the file like the old compiler did.
      const prepare = (scss) =>
        _this
          .fixModernRgb(scss)
          .replace(/@import\s+(["'])([^"']+)\.css\1\s*;/g, "@import $1$2$1;");

      // `@import "layout/header"` is canonicalised to theme:///layout/header
      // and loaded from the template's repository.
      const importer = {
        canonicalize(url) {
          if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
            return null;
          }
          return new URL("theme:///" + url.replace(/^\.\//, ""));
        },
        async load(canonicalUrl) {
          const url = canonicalUrl.pathname.replace(/^\//, "");
          if (_this.loaded.var > 98) {
            _this.loaded.var = 99;
          } else if (_this.loaded.var > 75) {
            _this.loaded.var += 1;
          } else {
            _this.loaded.var += 5;
          }
          _this.loaded.bar.set(_this.loaded.var);

          if (url === "variables") {
            const variables = await _this.getGlobalSettings();
            let rootCss = ":root {\n";
            let varCss = "";
            for (const key in variables) {
              rootCss += `--${key}: ${variables[key].value};\n`;
              varCss += `$${key}: var(--${key});\n`;
            }
            rootCss += "}";
            return { contents: rootCss + " " + varCss, syntax: "scss" };
          }

          // vendor files are plain .css, the rest are .scss partials
          let candidates = [`${url}.scss`, `${url}.css`];
          if (url.indexOf(".scss") > 0 || url.indexOf(".css") > 0) {
            candidates = [url];
          } else if (url.startsWith("vendor/")) {
            candidates = [`${url}.css`, `${url}.scss`];
          }
          for (const candidate of candidates) {
            try {
              return {
                contents: prepare(await fetchFile(`css/${candidate}`)),
                syntax: "scss",
              };
            } catch (e) {
              // try the next spelling
            }
          }
          // A missing import must not hang the whole compile.
          console.warn("SASS import not found:", url);
          return { contents: "", syntax: "scss" };
        },
      };

      fetchFile("css/app.scss")
        .then((scss) =>
          sass.compileStringAsync(prepare(scss), {
            importers: [importer],
            logger: sass.Logger.silent,
          })
        )
        .then((result) => {
          if (!result?.css?.length) {
            throw new Error("empty CSS");
          }
          _this.generatedCss = result.css;
          localStorage.setItem(cacheKey, result.css);
          _this.setCss();
        })
        .catch((error) => {
          // a theme whose SCSS will not compile is a real failure - keep the
          // report, on the error channel; don't cache it, unblock loading
          console.error("Theme CSS failed to compile:", error);
          _this.loaded.style = true;
          if (_this.loaded.var < 50) {
            _this.loaded.var = 50;
          }
          _this.setScript();
        });
    },
  },
};
</script>
<style scoped>
/* ---- Collapsible section ---- */
.disclosure {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 4px 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #94a3b8;
  cursor: pointer;
}
.disclosure:hover {
  color: #5b21b6;
}
.disclosure-caret {
  flex: none;
  transition: transform 0.15s;
}
.disclosure-caret.open {
  transform: rotate(90deg);
}

/* ---- Style option with few choices ---- */
.style-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.style-field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #94a3b8;
}
.seg {
  display: flex;
  padding: 3px;
  gap: 2px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  background: #f8fafc;
}
.seg-btn {
  flex: 1 1 0;
  min-width: 0;
  border: 0;
  background: transparent;
  border-radius: 7px;
  padding: 6px 6px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s;
}
.seg-btn:hover {
  color: #5b21b6;
}
.seg-btn.active {
  background: #fff;
  color: #5b21b6;
  box-shadow: 0 1px 3px rgba(18, 18, 18, 0.12);
}

/* "Give a region its own version" — a small menu rather than a dropdown of
   every currency, because the regions not listed are not separate things. */
.region-add {
  position: relative;
}
.region-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 40;
  min-width: 170px;
  padding: 6px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(18, 18, 18, 0.12);
}
.region-menu-head {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  padding: 4px 8px 6px;
}
.region-menu button {
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 7px 8px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
}
.region-menu button:hover {
  background: #ede9fe;
  color: #5b21b6;
}

.module-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.module-icon {
  width: 36px;
  height: 36px;
  flex: none;
  border-radius: 9px;
  background: rgba(124, 58, 237, 0.12);
  color: #7c3aed;
  display: grid;
  place-items: center;
}
.module-icon :deep(svg) {
  width: 19px;
  height: 19px;
  fill: currentColor;
}
.module-title {
  flex: 1;
  min-width: 0;
}
.module-title h2 {
  font-size: 16px;
  font-weight: 700;
}
.module-actions {
  display: flex;
  gap: 2px;
  flex: none;
}
.array-item {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px;
  margin-bottom: 6px;
  list-style: none;
}
.array-item-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.array-item-thumb {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  flex: none;
  background: #f1f5f9;
}
.array-item-thumb.small {
  width: 28px;
  height: 28px;
  border-radius: 6px;
}
.array-item-thumb-empty {
  display: grid;
  place-items: center;
  color: #c0bdbd;
}
.array-item-title {
  font-size: 13px;
  font-weight: 600;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.array-item-actions {
  display: flex;
  gap: 2px;
  flex: none;
}
.nested-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 0 4px 0;
}
.nested-subgroup {
  width: 100%;
}
.nested-drag {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.nested-row {
  padding: 3px 4px;
  border-radius: 8px;
}
.nested-row:hover {
  background: #f1f5f9;
}
.device-switcher {
  margin-left: auto;
}
.drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(18, 18, 18, 0.35);
  z-index: 1050;
  animation: omFade 0.15s ease;
}
.drawer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(340px, 92vw);
  background: #fff;
  box-shadow: -8px 0 30px rgba(18, 18, 18, 0.2);
  display: flex;
  flex-direction: column;
  animation: drawer-in 0.25s ease;
}
@keyframes drawer-in {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
.drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}
.drawer-head h2 {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}
.drawer-body {
  padding: 14px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.version-row {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 11px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.version-info {
  flex: 1;
  min-width: 0;
}
.version-label {
  font-size: 13px;
  font-weight: 700;
}
.version-when {
  font-size: 11px;
  color: #768692;
  font-weight: 600;
}
</style>
